// ═══════════════════════════════════════════════════════════════
// useLiveData.js — PumpFun websocket with wallet-level tracking
// Builds holder count, dev wallet %, distribution from trade stream
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from "react";
import { connectPumpFun, fetchTokenByAddress, fetchTokensBatch, fetchTokenMeta, fetchHolderCount, fetchHolderCountPublic, fetchLargestHolders, fetchLatestProfiles, fetchBoostedTokens, connectHeliusWS, fetchRugCheck, fetchGeckoTrending, fetchGeckoPoolByToken, fetchPumpCurveProgress, fetchDefinedTrending, fetchJupiterSlippage, fetchJupiterVerified, fetchPumpFunDirect, fetchRaydiumPool, fetchUniqueSigners } from "./api";

// ─── DIRECT SUPABASE WRITES (no callback chain needed) ───
var SB_URL = "https://yrmjphhfgduysoftnuxv.supabase.co";
var SB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlybWpwaGhmZ2R1eXNvZnRudXh2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3MzI5MzAsImV4cCI6MjA4ODMwODkzMH0.scHhvTGiABJDybgbjgjilw8XuxOfmWPsqo4iytMZmio";

async function sbUpsertToken(token) {
  if (!token?.addr) return;
  try {
    // Detect rug: died fast (< 5 min) at low mcap (< 20k), or peak < 5k
    const lifeMs = token.deathTime ? token.deathTime - (token.timestamp || 0) : null;
    const isRug = token.alive === false && (
      (lifeMs && lifeMs < 300000 && (token.peakMcap || 0) < 20000) ||
      (token.peakMcap || 0) < 3000
    );
    const r = await fetch(`${SB_URL}/rest/v1/token_history?on_conflict=addr`, {
      method: "POST",
      headers: { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}`, "Content-Type": "application/json", Prefer: "resolution=merge-duplicates" },
      body: JSON.stringify([{
        addr: token.addr, name: token.name || "???",
        peak_mcap: token.peakMcap || token.mcap || 0,
        entry_mcap: token.entryMcap || token.mcap || 0,
        death_time: token.alive === false ? Date.now() : null,
        first_seen: token.timestamp || Date.now(),
        graduated: token.migrated || false,
        platform: token.platform || "PumpFun",
        holders: token.holders || 0,
        volume: token.vol || 0,
        deployer: token.deployer || "",
        rug: isRug,
        image_uri: token.imageUri || "",
        updated_at: new Date().toISOString(),
      }]),
    });
    console.log(`[SB] 🪙 ${token.name} → ${r.ok ? "✅" : "❌ " + r.status}`);
  } catch(e) { console.warn("[SB] upsertToken failed:", e.message); }
}

async function sbUpsertWallet(addr, ws) {
  if (!addr || !ws) return;
  try {
    const r = await fetch(`${SB_URL}/rest/v1/wallet_scores?on_conflict=addr`, {
      method: "POST",
      headers: { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}`, "Content-Type": "application/json", Prefer: "resolution=merge-duplicates" },
      body: JSON.stringify([{
        addr, wins: ws.wins || 0, losses: ws.losses || 0, holds: ws.holds || 0,
        total_pnl: ws.totalPnl || 0, total_bought: ws.totalBought || 0,
        total_sold: ws.totalSold || 0, big_wins: ws.bigWins || 0,
        trades: (ws.trades || []).slice(-50),
        win_addrs: ws.winAddrs ? [...ws.winAddrs] : [],
        loss_addrs: ws.lossAddrs ? [...ws.lossAddrs] : [],
        updated_at: new Date().toISOString(),
      }]),
    });
    if (!r.ok) console.warn(`[SB] wallet ${addr.slice(0,8)} failed:`, r.status);
  } catch(e) { console.warn("[SB] upsertWallet failed:", e.message); }
}


var _LIVE_COIN_COLORS = [
  { bg: "#ff6b35", fg: "#fff", rim: "#cc5528" },
  { bg: "#00d4aa", fg: "#fff", rim: "#00a885" },
  { bg: "#7c4dff", fg: "#fff", rim: "#6237cc" },
  { bg: "#ff4081", fg: "#fff", rim: "#cc3367" },
  { bg: "#448aff", fg: "#fff", rim: "#366ecc" },
  { bg: "#ffd740", fg: "#222", rim: "#ccac33" },
  { bg: "#69f0ae", fg: "#111", rim: "#54c08b" },
  { bg: "#ff5252", fg: "#fff", rim: "#cc4141" },
  { bg: "#40c4ff", fg: "#fff", rim: "#339dcc" },
  { bg: "#b388ff", fg: "#fff", rim: "#8f6dcc" },
  { bg: "#ff9100", fg: "#fff", rim: "#cc7400" },
  { bg: "#00e5ff", fg: "#111", rim: "#00b7cc" },
  { bg: "#e040fb", fg: "#fff", rim: "#b333c9" },
  { bg: "#76ff03", fg: "#111", rim: "#5ecc02" },
  { bg: "#ff6e40", fg: "#fff", rim: "#b34828" },
  { bg: "#18ffff", fg: "#111", rim: "#10b0b0" },
];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function formatNum(n) { if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return n.toFixed(0); }

var SOL_USD = 84; // default, updated live from Jupiter every 30s
var MCAP_CORRECTION = 1.0; // auto-calibrated: PumpPortal FDV → DexScreener mcap ratio
var mcapSamples = []; // stores {ppMcap, dexMcap} pairs for calibration

// Convert tradeData's lastMcapSol to USD, applying correction only for PumpPortal-sourced values
function tdMcapUsd(td) {
  if (!td || !td.lastMcapSol) return 0;
  return td.mcapSource === "dex"
    ? td.lastMcapSol * SOL_USD
    : td.lastMcapSol * SOL_USD * MCAP_CORRECTION;
}

export function useLiveData({ onMarkDirty, onSmartAlert, onUpsertToken } = {}) {
  const [tokens, setTokens] = useState([]);
  const tokensRef = useRef([]); // synchronous access for intervals
  tokensRef.current = tokens; // sync during render — always up to date

  // Keep callback refs always up to date — avoids stale closure in useEffect intervals
  const onMarkDirtyRef = useRef(onMarkDirty);
  const onSmartAlertRef = useRef(onSmartAlert);
  const onUpsertTokenRef = useRef(onUpsertToken);
  useEffect(() => { onMarkDirtyRef.current = onMarkDirty; }, [onMarkDirty]);
  useEffect(() => { onSmartAlertRef.current = onSmartAlert; }, [onSmartAlert]);
  useEffect(() => { onUpsertTokenRef.current = onUpsertToken; }, [onUpsertToken]);
  const [whaleAlerts, setWhaleAlerts] = useState([]);
  const [intelEvents, setIntelEvents] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [stats, setStats] = useState({ scanned: 0, deployed: 0, rejected: 0 });

  // ─── NEW INTEL SYSTEMS ───
  const [smartMoneyAlerts, setSmartMoneyAlerts] = useState([]);
  const [bundleAlerts, setBundleAlerts] = useState([]);
  const [narratives, setNarratives] = useState([]);
  const [sessionStats, setSessionStats] = useState({
    startTime: Date.now(), tokensScanned: 0, qualified: 0, locked: 0, ejected: 0,
    migrations: 0, bestToken: null, bestPct: 0, avgLifespan: 0, lifespans: [],
  });

  // Smart money: wallets that have been in winning tokens
  const walletScores = useRef({}); // { wallet: { wins, losses, tokens: [], lossTokens: [], totalBought: 0, bigWins: 0, trades: [{token,sol,type,mcap,time}] } }
  const copyTradeAlerts = useRef(new Set()); // prevent dupes

  // Per-token tracking with wallet-level detail
  const tradeData = useRef({});
  const mintToId = useRef({});
  const seenMints = useRef(new Set());
  const firedEvents = useRef(new Set());
  const migratedMints = useRef(new Set());
  const heliusWsRef = useRef(null);        // Helius WebSocket for post-migration live tracking
  const bondingAlerted = useRef(new Set()); // track bonding alerts to avoid state mutation
  const rugCheckCache = useRef({});         // {mint: rugCheckResult}
  const geckoTrendingRef = useRef([]);      // GeckoTerminal trending pool addresses
  const definedTrendingRef = useRef([]);    // Defined.fi trending token addresses
  const curveProgressCache = useRef({});    // {mint: {pct, ts}}
  const jupVerifiedRef = useRef(new Set()); // Jupiter verified token list
  const slippageCache = useRef({});         // {mint: {slippage, ts}}
  const pumpDirectCache = useRef({});       // {mint: {data, ts}}
  const raydiumPoolCache = useRef({});      // {mint: {pool, ts}}
  const activityCache = useRef({});         // {mint: {signers, ts}}

  // ─── NEURAL WEB: Flash boards + Signal Scores ───
  const flashSnapsRef = useRef({});  // addr -> [{time, mcap}] for 30s/1m/5m boards
  const fastSnapRef = useRef({});    // addr -> [{time, mcap}] from tradeData (5s interval, fills board gaps)
  const signalScores = useRef({});   // addr -> 0-100 composite signal score
  const externalBoostRef = useRef({}); // addr -> boost from intel (clusters, DNA) — injected by useIntelligence
  const [flashBoard30s, setFlashBoard30s] = useState([]);
  const [flashBoard1m, setFlashBoard1m] = useState([]);
  const [flashBoard5m, setFlashBoard5m] = useState([]);
  const [autoSurface, setAutoSurface] = useState([]); // [{addr,name,score,reasons,time}] — tokens that auto-surfaced

  // ─── EDGE DETECTION GLOBALS ───
  const globalWallets = useRef(new Set());   // every wallet we've ever seen trade
  const deployerHistory = useRef({});         // { deployer: { launches: N, rugs: N, mints: [] } }

  useEffect(() => {
    const pf = connectPumpFun(
      // ─── NEW TOKEN ───
      (newToken) => {
        if (seenMints.current.has(newToken.mint)) return;
        seenMints.current.add(newToken.mint);
        if (seenMints.current.size > 600) {
          seenMints.current = new Set([...seenMints.current].slice(-500));
        }

        const name = newToken.symbol || newToken.name || "???";
        const mcap = (newToken.marketCapSol || 0) * SOL_USD * MCAP_CORRECTION;
        const id = newToken.mint + Date.now();

        console.log(`[LIVE] 🆕 ${name} | mcap: $${formatNum(mcap)} | ${newToken.mint.slice(0, 12)}...`);

        // Subscribe to this token's trades
        if (pf.subscribeToken) pf.subscribeToken(newToken.mint);

        // Track deployer history
        const dep = newToken.deployer || "";
        if (dep) {
          if (!deployerHistory.current[dep]) deployerHistory.current[dep] = { launches: 0, rugs: 0, mints: [] };
          deployerHistory.current[dep].launches++;
          deployerHistory.current[dep].mints.push(newToken.mint);
        }

        // Init comprehensive trade tracking with edge detection
        tradeData.current[newToken.mint] = {
          buys: 0, sells: 0, volSol: 0, lastMcapSol: newToken.marketCapSol || 0, mcapSource: "pp",
          deployer: dep,
          wallets: {},
          deployerSold: false,
          deployerSellPct: 0,
          totalBoughtSol: 0,
          totalSoldSol: 0,
          startMcap: (newToken.marketCapSol || 0) * SOL_USD * MCAP_CORRECTION, // mcap at birth
          athMcap: (newToken.marketCapSol || 0) * SOL_USD * MCAP_CORRECTION,   // tracks all-time high
          // ─── EDGE DETECTION ───
          freshBuyers: 0,          // buyers never seen before
          totalBuyers: 0,          // total unique buyers
          buyTimes: [],            // last 60 buy timestamps for velocity
          buySizes: [],            // last 60 buy sizes in SOL
          launchTime: Date.now(),  // token creation time
          lastTradeTime: Date.now(),  // track staleness
          sellTimes: [],              // last 60 sell timestamps
          milestones: {},          // { "10k": ms_elapsed, "20k": ms_elapsed, ... }
          earlyBuyers: {},         // first 15 buyers: wallet -> { initBuy, totalBought, totalSold }
          earlyBuyerCount: 0,
          holderSnapshots: [],      // [{time, count}] for growth rate
          mcapSnapshots: [],        // [{time, mcap}] for trajectory
        };
        mintToId.current[newToken.mint] = id;

        const token = {
          id, addr: newToken.mint, name, fullName: newToken.name || name,
          platform: newToken.platform || "PumpFun", mcap, vol: 0, priceUsd: 0, liquidity: mcap * 0.1,
          holders: 1, devWallet: 0, buys: 0, sells: 0,
          riskScore: 30, qualified: false, qualScore: 0, qualChecks: [],
          threat: "SCANNING", threatColor: "#ffe600",
          bundleDetected: false, bundleSize: 0, mintAuth: false,
          topHolderPct: 0, dupeCount: 0,
          bx: Math.random() * 0.8 + 0.1, by: 0.92, targetY: 0.92,
          vx: (Math.random() - 0.5) * 0.0006, health: 50, alive: true,
          age: 0, trail: [], warpIn: false, warpProgress: 1,
          warpStartX: Math.random(), warpStartY: Math.random() * 0.2 - 0.1,
          bobOffset: Math.random() * Math.PI * 2,
          initials: name.slice(0, 2).toUpperCase(),
          coinColor: pick(_LIVE_COIN_COLORS),
          timestamp: Date.now(), deployer: newToken.deployer || "",
          imageUri: newToken.imageUri || "",
          jupVerified: jupVerifiedRef.current.has(newToken.mint),
        };

        setTokens(prev => {
          let list = [token, ...prev];
          // Purge aggressively — SOL tokens come and go fast
          const now2 = Date.now();
          list = list.filter(t => {
            if (t.migrated) return true;
            if (!t.alive && now2 - (t.deathTime || t.timestamp) > 45000) return false;
            if (t.alive && !t.qualified && t.buys <= 0 && t.sells <= 0 && now2 - t.timestamp > 30000) return false;
            if (t.alive && !t.migrated && (t.mcap || 0) < 3000 && (t.buys || 0) < 3 && now2 - t.timestamp > 90000) return false;
            return true;
          });
          return list.slice(0, 150);
        });
        setStats(s => ({ ...s, scanned: s.scanned + 1 }));

        setIntelEvents(prev => [{
          id: Date.now() + Math.random(), type: "launch", icon: "🚀", color: "#00ffff",
          text: `${name} launched | mcap $${formatNum(mcap)} | deployer: ${(newToken.deployer || "").slice(0, 8)}...`,
          timestamp: Date.now(), priority: "NORMAL",
        }, ...prev].slice(0, 40));
      },

      // ─── TRADE ───
      (trade) => {
        const mint = trade.mint;
        const td = tradeData.current[mint];
        if (!td) return;

        const wallet = trade.wallet || "";
        const sol = trade.solAmount || 0;
        const now = Date.now();

        td.lastTradeTime = now;  // track staleness

        // Track per-wallet activity
        if (!td.wallets[wallet]) td.wallets[wallet] = { bought: 0, sold: 0, entryMcap: 0, firstBuyTime: now, sellEvents: [] };

        if (trade.type === "buy") {
          // ─── CYCLE RESET: if wallet previously fully exited this token, start fresh ───
          const wData = td.wallets[wallet];
          if (wData && wData.bought > 0) {
            const prevSoldRatio = wData.bought > 0 ? (wData.sold || 0) / wData.bought : 0;
            if (prevSoldRatio >= 0.90) {
              // New buy cycle — wipe old position data
              td.wallets[wallet] = { bought: 0, sold: 0, entryMcap: 0, firstBuyTime: now, lastSellTime: null, exitMcap: 0, sellEvents: [] };
              // Also clear from wallet scores so new HOLD can be tracked cleanly
              const wsNow = walletScores.current[wallet];
              if (wsNow) {
                // Remove from holdAddrs so scoring loop treats this as a new position
                if (wsNow.holdAddrs?.has(mint)) {
                  wsNow.holdAddrs.delete(mint);
                  wsNow.holds = Math.max(0, (wsNow.holds||0) - 1);
                  wsNow.holdTokens = (wsNow.holdTokens||[]).filter(a => a !== mint);
                  // Keep the old resolved HOLD trade in history but allow new one
                  const oldHold = wsNow.trades?.find(tr => tr.addr === mint && tr.type === "HOLD");
                  if (oldHold) oldHold.type = "CLOSED_HOLD"; // archive it, stop refreshing
                }
                // Reset activeBuys for fresh entry
                if (wsNow.activeBuys) delete wsNow.activeBuys[mint];
              }
            }
          }
          td.buys++;
          td.totalBoughtSol += sol;
          td.wallets[wallet].bought += sol;
          if (!td.wallets[wallet].firstBuyTime) td.wallets[wallet].firstBuyTime = now;
          // Store entry mcap on first buy for this wallet on this token
          if (!td.wallets[wallet].entryMcap) {
            const mcapNow = (trade.marketCapSol || td.lastMcapSol || 0) * SOL_USD * MCAP_CORRECTION;
            td.wallets[wallet].entryMcap = mcapNow;
          }

          // ─── FRESH WALLET DETECTION ───
          td.totalBuyers++;
          if (!globalWallets.current.has(wallet)) {
            td.freshBuyers++;
          }

          // ─── BUY VELOCITY ───
          td.buyTimes.push(now);
          if (td.buyTimes.length > 60) td.buyTimes = td.buyTimes.slice(-60);

          // ─── BUY SIZE TRACKING ───
          td.buySizes.push(sol);
          if (td.buySizes.length > 60) td.buySizes = td.buySizes.slice(-60);

          // ─── EARLY BUYER TRACKING (first 15 buyers) ───
          if (td.earlyBuyerCount < 15 && !td.earlyBuyers[wallet]) {
            td.earlyBuyers[wallet] = { initBuy: sol, totalBought: sol, totalSold: 0 };
            td.earlyBuyerCount++;
          } else if (td.earlyBuyers[wallet]) {
            td.earlyBuyers[wallet].totalBought += sol;
          }

          // ─── BUNDLE DETECTION: 4+ unique wallets buying within 2s ───
          if (!td.recentBuyers) td.recentBuyers = [];
          td.recentBuyers.push({ wallet, time: now });
          td.recentBuyers = td.recentBuyers.filter(b => now - b.time < 2000);
          const uniqueRecent = new Set(td.recentBuyers.map(b => b.wallet));
          if (uniqueRecent.size >= 4 && !td.bundleDetected) {
            td.bundleDetected = true;
            td.bundleWallets = new Set(uniqueRecent); // FROZEN — only the original coordinated wallets
            td.bundleSize = uniqueRecent.size;
            const tokenName = mintToId.current[mint] ?
              undefined : mint.slice(0, 8);
            setBundleAlerts(p => [{
              id: Date.now() + Math.random(), mint, wallets: uniqueRecent.size,
              time: now, name: tokenName,
            }, ...p].slice(0, 30));
            console.log(`[BUNDLE] ⚠ ${mint.slice(0,8)} — ${uniqueRecent.size} wallets in 2s`);
          }
          // NOTE: no longer adds future buyers to bundleWallets — set is frozen at detection

          // ─── SMART MONEY: check if buying wallet has wins + win rate ───
          if (!td.smartWallets) td.smartWallets = new Set();
          // Create walletScores entry immediately on first buy so lastActivity is fresh
          if (!walletScores.current[wallet]) {
            walletScores.current[wallet] = {
              wins: 0, losses: 0, holds: 0,
              tokens: [], lossTokens: [], holdTokens: [],
              winAddrs: new Set(), lossAddrs: new Set(), holdAddrs: new Set(),
              totalBought: 0, totalSold: 0, totalPnl: 0, bigWins: 0, trades: [],
              lastActivity: now, activeBuys: {},
            };
          }
          const ws = walletScores.current[wallet];
          // Update lastActivity on actual trade (not scoring loop)
          if (ws) ws.lastActivity = now;

          // ─── TRACK ACTIVE POSITION so it shows in wallet detail immediately ───
          if (ws) {
            if (!ws.activeBuys) ws.activeBuys = {};
            const entryMcNow = tdMcapUsd(td);
            if (!ws.activeBuys[mint]) {
              const _tid = mintToId.current[mint];
              const _tname = _tid ? (tokensRef.current.find(t2=>t2.id===_tid)?.name || mint.slice(0,8)) : mint.slice(0,8);
              ws.activeBuys[mint] = { token: _tname, addr: mint, sol: 0, entryMcap: entryMcNow||0, time: now };
            }
            ws.activeBuys[mint].sol += sol;
            ws.activeBuys[mint].entryMcap = ws.activeBuys[mint].entryMcap || tdMcapUsd(td) || 0;
          }
          // Compute unrealized value from open holds (use stored unrealizedPnl field if available)
          const unrealizedPnl = ws ? (ws.trades||[]).filter(tr=>tr.type==="HOLD")
            .reduce((s,tr)=>s+(tr.unrealizedPnl!=null?tr.unrealizedPnl:(tr.pnl!=null?tr.pnl:0)),0) : 0;
          const qualWinCount = ws ? (ws.trades||[]).filter(tr=>tr.type==="WIN").length : 0;
          const qualLossCount = ws ? (ws.trades||[]).filter(tr=>tr.type==="LOSS").length : 0;
          const qualTotal = qualWinCount + qualLossCount;
          const qualRate = qualTotal > 0 ? qualWinCount / qualTotal : 0;
          const adjustedPnl = ws ? (ws.totalPnl||0) + unrealizedPnl : 0;
          const isElite = ws && qualWinCount >= 3 && qualRate >= 0.60 && qualWinCount >= (qualLossCount*2) && qualTotal >= 4 && (ws.totalPnl||0) >= 0.5 && adjustedPnl > 0.2 && sol > 0.2;
          if (isElite) {
            td.smartWallets.add(wallet);
            const alertKey = `${wallet.slice(0,8)}-${mint}`;
            if (!copyTradeAlerts.current.has(alertKey)) {
              copyTradeAlerts.current.add(alertKey);
              const alertObj = {
                id: Date.now() + Math.random(), wallet, mint, sol,
                wins: ws.wins, winTokens: ws.tokens.slice(-3),
                time: now,
              };
              setSmartMoneyAlerts(p => [alertObj, ...p].slice(0, 30));
              if (onSmartAlertRef.current) onSmartAlertRef.current(alertObj);
              console.log(`[SMART$] 🧠 Winner wallet (${ws.wins}w) bought ${sol.toFixed(2)} SOL into ${mint.slice(0,8)}`);
            }
          }
        } else {
          td.sells++;
          td.totalSoldSol += sol;
          td.wallets[wallet].sold += sol;

          // Update lastActivity on sell
          if (walletScores.current[wallet]) walletScores.current[wallet].lastActivity = now;
          td.wallets[wallet].lastSellTime = now;
          // Capture mcap at time of sell for accurate exit display
          const sellMcapNow = tdMcapUsd(td);
          td.wallets[wallet].exitMcap = sellMcapNow;

          // Track individual sell events for trade card breakdown
          if (!td.wallets[wallet].sellEvents) td.wallets[wallet].sellEvents = [];
          td.wallets[wallet].sellEvents.push({ sol, mcap: sellMcapNow, time: now });

          // ─── SELL VELOCITY ───
          td.sellTimes.push(now);
          if (td.sellTimes.length > 60) td.sellTimes = td.sellTimes.slice(-60);

          // Update early buyer sells
          if (td.earlyBuyers[wallet]) {
            td.earlyBuyers[wallet].totalSold += sol;
          }
        }

        // Mark wallet as globally seen
        globalWallets.current.add(wallet);
        // Cap global set size
        if (globalWallets.current.size > 50000) {
          const arr = [...globalWallets.current];
          globalWallets.current = new Set(arr.slice(-30000));
        }

        td.volSol += sol;
        if (trade.marketCapSol > 0) {
          // ─── MILESTONE TRACKING ───
          const oldMcap = td.mcapSource === "dex" ? td.lastMcapSol * SOL_USD : td.lastMcapSol * SOL_USD * MCAP_CORRECTION;
          td.lastMcapSol = trade.marketCapSol;
          td.mcapSource = "pp"; // PumpPortal raw value, needs correction
          const newMcapUsd = trade.marketCapSol * SOL_USD * MCAP_CORRECTION;
          // ─── ATH TRACKING ───
          if (!td.athMcap || newMcapUsd > td.athMcap) td.athMcap = newMcapUsd;
          const elapsed = now - td.launchTime;
          [10000, 20000, 30000, 50000].forEach(m => {
            if (!td.milestones[m] && newMcapUsd >= m && oldMcap < m) {
              td.milestones[m] = elapsed;
            }
          });
        }

        // Track deployer sells
        if (wallet === td.deployer && trade.type === "sell") {
          td.deployerSold = true;
          const devBought = td.wallets[wallet].bought;
          td.deployerSellPct = devBought > 0
            ? (td.wallets[wallet].sold / devBought) * 100
            : 100;
        }

        // Compute live metrics
        const uniqueWallets = Object.keys(td.wallets).length;
        const newMcap = tdMcapUsd(td);
        const volUsd = td.volSol * SOL_USD;

        // Top holder concentration: biggest NET holder's % of total net bought
        let maxNet = 0;
        let totalNet = 0;
        Object.values(td.wallets).forEach(w => {
          const net = Math.max(0, w.bought - w.sold);
          if (net > maxNet) maxNet = net;
          totalNet += net;
        });
        const topHolderPct = totalNet > 0 ? (maxNet / totalNet) * 100 : 0;

        // Dev wallet % = deployer's net holding as % of total volume
        const deployerNet = td.wallets[td.deployer]
          ? td.wallets[td.deployer].bought - td.wallets[td.deployer].sold : 0;
        const devPct = td.totalBoughtSol > 0 ? Math.max(0, (deployerNet / td.totalBoughtSol) * 100) : 0;

        // Liquidity estimate from volume
        const liq = volUsd * 0.3; // rough estimate

        // Update token (+ resurrect if dead but getting new buys)
        const tokenId = mintToId.current[mint];
        if (tokenId) {
          setTokens(prev => prev.map(t => {
            if (t.id !== tokenId) return t;
            // Revival conditions: dead token getting real traction back
            const canRevive = !t.alive && trade.type === "buy" && td.buys > td.sells + 2 && newMcap > 5000;
            // Reset resurrection flag after 60s so tokens can revive again
            if (td._resurrected && td._resurrectTime && Date.now() - td._resurrectTime > 60000) {
              td._resurrected = false;
            }
            if (canRevive && !td._resurrected) {
              td._resurrected = true;
              td._resurrectTime = Date.now();
              setIntelEvents(p => [{
                id: Date.now() + Math.random(), type: "momentum", icon: "🔥", color: "#ffd740",
                text: `${t.name} RESURRECTED — ${t.migrated?"migrated token":"token"} fighting back at $${formatNum(newMcap)}`,
                timestamp: Date.now(), priority: "HIGH",
              }, ...p].slice(0, 40));
            }
            return {
              ...t,
              buys: td.buys, sells: td.sells,
              vol: volUsd,
              mcap: newMcap > 0 ? newMcap : t.mcap,
              athMcap: Math.max(t.athMcap || 0, newMcap > 0 ? newMcap : 0, td.athMcap || 0),
              holders: uniqueWallets,
              topHolderPct: Math.round(topHolderPct),
              devWallet: Math.round(devPct),
              liquidity: liq,
              ...(canRevive ? { alive: true, health: 45, by: 0.92, bx: Math.random()*0.8+0.1 } : {}),
            };
          }));
        }

        // Whale alerts
        if (sol > 2) {
          const wTokenName = tokenId ? (tokensRef.current.find(t2=>t2.id===tokenId)?.name || mint.slice(0,8)+"...") : mint.slice(0,8)+"...";
          setWhaleAlerts(prev => [{
            id: Date.now() + Math.random(),
            wallet: wallet.slice(0, 12) + "...",
            action: trade.type === "buy" ? "BUY" : "SELL",
            token: wTokenName,
            tokenMint: mint.slice(0, 8) + "...",
            amount: sol * SOL_USD,
            solAmount: sol,
            timestamp: Date.now(),
          }, ...prev].slice(0, 20));
        }

        if (sol > 10) {
          setIntelEvents(prev => [{
            id: Date.now() + Math.random(),
            type: trade.type === "buy" ? "whale" : "dump",
            icon: trade.type === "buy" ? "🐋" : "📉",
            color: trade.type === "buy" ? "#bf00ff" : "#ff073a",
            text: `${sol.toFixed(1)} SOL ${trade.type} on ${mint.slice(0, 8)}...`,
            timestamp: Date.now(),
            priority: sol > 50 ? "HIGH" : "NORMAL",
          }, ...prev].slice(0, 40));
        }

        // Deployer sell alert
        if (wallet === td.deployer && trade.type === "sell" && sol > 0.5) {
          const tokenId2 = mintToId.current[mint];
          const tokenName = tokensRef.current.find(t2 => t2.id === tokenId2)?.name || mint.slice(0, 8);
          setIntelEvents(prev => [{
            id: Date.now() + Math.random(), type: "dump", icon: "🔴", color: "#ff073a",
            text: `DEV SELLING ${tokenName} — ${sol.toFixed(1)} SOL dumped by deployer`,
            timestamp: Date.now(), priority: "HIGH",
          }, ...prev].slice(0, 40));
        }
      },

      // ─── MIGRATION: token graduated to Raydium ───
      (migration) => {
        if (migratedMints.current.has(migration.mint)) return;
        migratedMints.current.add(migration.mint);

        // Subscribe to Helius WebSocket for real-time swap detection
        if (heliusWsRef.current) {
          heliusWsRef.current.subscribe(migration.mint);
          console.log(`[MIGRATE] 📡 Helius WS subscribed to ${migration.mint.slice(0, 8)}`);
        }

        // Run RugCheck in background (non-blocking)
        fetchRugCheck(migration.mint).then(rc => {
          if (!rc) return;
          rugCheckCache.current[migration.mint] = rc;
          console.log(`[RUGCHECK] 🛡 ${migration.mint.slice(0, 8)} → score:${rc.score} level:${rc.riskLevel} mint:${rc.mintAuthority} freeze:${rc.freezeAuthority}`);
          // Update token with rugcheck data
          const tid = mintToId.current[migration.mint];
          if (tid) {
            setTokens(prev => prev.map(t => t.id === tid ? {
              ...t,
              rugScore: rc.score,
              rugLevel: rc.riskLevel,
              rugRisks: rc.risks,
              lpLocked: rc.lpLocked,
            } : t));
          }
          // Update migration entry
          setMigrations(prev => prev.map(m => m.mint === migration.mint ? {
            ...m, rugScore: rc.score, rugLevel: rc.riskLevel, lpLocked: rc.lpLocked,
          } : m));
        }).catch(e => console.warn("[RUGCHECK] Failed:", e.message));

        const tokenId = mintToId.current[migration.mint];
        const td = tradeData.current[migration.mint];
        const mcapUsd = (migration.marketCapSol || 0) * SOL_USD * MCAP_CORRECTION;

        console.log(`[MIGRATE] 🌉 FIRING for ${migration.mint.slice(0,8)} | tokenId:${tokenId ? "YES" : "NO"} | mcapSol:${migration.marketCapSol} | mcapUsd:$${formatNum(mcapUsd)}`);

        // Find the token name
        let tokenName = migration.mint.slice(0, 8) + "...";
        const migTok = tokensRef.current.find(t => t.id === tokenId);
        if (migTok) tokenName = migTok.name;
        else console.warn(`[MIGRATE] ⚠ Token ${migration.mint.slice(0,8)} not found in tokensRef! tokenId:${tokenId}`);

        console.log(`[LIVE] 🌉 MIGRATION: ${tokenName} graduated to Raydium | mcap: $${formatNum(mcapUsd)}`);

        // Add to migrations list (deduplicate by mint)
        setMigrations(prev => {
          if(prev.some(m=>m.mint===migration.mint)) return prev;
          return [{
          id: Date.now() + Math.random(),
          mint: migration.mint,
          tokenId,
          name: tokenName,
          mcap: mcapUsd,
          vol: td ? td.volSol * SOL_USD : 0,
          holders: td ? Object.keys(td.wallets || {}).length : 0,
          timestamp: Date.now(),
          laserActive: true,
        }, ...prev].slice(0, 20)});

        // Mark token as migrated — enters battlefield with laser from radar
        if (tokenId) {
          // Calculate correct Y position from mcap (same formula as battlefield progression)
          // Piecewise Y matching battlefield zones
          const mc2 = mcapUsd;
          const zz=[[5000,0.95],[10000,0.63],[20000,0.47],[50000,0.32],[100000,0.20],[300000,0.10]];
          let entryY=0.95;
          if(mc2>=300000)entryY=0.05;
          else{for(let i=0;i<zz.length-1;i++){
            if(mc2>=zz[i][0]&&mc2<zz[i+1][0]){
              const pct=(mc2-zz[i][0])/(zz[i+1][0]-zz[i][0]);
              entryY=zz[i][1]+(zz[i+1][1]-zz[i][1])*pct;break;}}}
          const landX = 0.3 + Math.random() * 0.35;
          setTokens(prev => prev.map(t => t.id === tokenId && !t.migrated ? {
            ...t, migrated: true, migratedAt: Date.now(),
            laserIn: true, laserProgress: 0,
            laserTargetX: landX, laserTargetY: entryY,
            bx: 1.02, by: 0.18, targetY: entryY,
            warpIn: false, alive: true,
            qualified: true, health: 95,
          } : t));
          // Write migration to DB
          if (onUpsertTokenRef.current) onUpsertTokenRef.current({
            addr: migration.mint, name: tokenName,
            mcap: mcapUsd, peakMcap: mcapUsd,
            timestamp: Date.now(), migrated: true,
            alive: true, platform: "Raydium",
          });
        }

        setIntelEvents(prev => [{
          id: Date.now() + Math.random(), type: "migration", icon: "🌉", color: "#39ff14",
          text: `${tokenName} MIGRATED TO RAYDIUM — $${formatNum(mcapUsd)} mcap — ON THE FIELD`,
          timestamp: Date.now(), priority: "HIGH",
        }, ...prev].slice(0, 40));
      }
    );

    // ─── QUALIFY TOKENS EVERY 5s ───
    const qualInterval = setInterval(() => {
      setTokens(prev => {
        const result = prev.map(t => {
        const td = tradeData.current[t.addr];
        if (!td || (td.buys + td.sells < 2)) return t;

        const buys = td.buys;
        const sells = td.sells;
        const volUsd = td.volSol * SOL_USD;
        const mcap = tdMcapUsd(td);
        const uniqueWallets = Object.keys(td.wallets).length;
        const volMcapRatio = mcap > 0 ? volUsd / mcap : 0;
        const isSuspicious = mcap > 50000 && volMcapRatio < 0.05;
        const now = Date.now();

        // Top holder concentration (net position, not raw buys)
        let maxNet2 = 0, totalNet2 = 0;
        Object.values(td.wallets).forEach(w => {
          const net = Math.max(0, w.bought - w.sold);
          if (net > maxNet2) maxNet2 = net;
          totalNet2 += net;
        });
        const topPct = totalNet2 > 0 ? (maxNet2 / totalNet2) * 100 : 0;

        // Dev wallet
        const deployerNet = td.wallets[td.deployer]
          ? td.wallets[td.deployer].bought - td.wallets[td.deployer].sold : 0;
        const devPct = td.totalBoughtSol > 0 ? Math.max(0, (deployerNet / td.totalBoughtSol) * 100) : 0;

        // ═══════════ EDGE DETECTION METRICS ═══════════

        // 1. FRESH WALLET % — high = probably coordinated
        const freshPct = td.totalBuyers > 0 ? (td.freshBuyers / td.totalBuyers) * 100 : 50;

        // 1b. SNAPSHOT COLLECTION — holder count and mcap over time
        const holderCount = Object.keys(td.wallets).filter(w => {
          const ww = td.wallets[w];
          return (ww.totalBought || 0) > (ww.totalSold || 0);
        }).length || 1;
        if(!td.holderSnapshots)td.holderSnapshots=[];
        if(!td.mcapSnapshots)td.mcapSnapshots=[];
        td.holderSnapshots.push({time: now, count: holderCount});
        td.mcapSnapshots.push({time: now, mcap: mcap});
        // Keep only last 2 minutes of snapshots
        td.holderSnapshots = td.holderSnapshots.filter(s => now - s.time < 120000);
        td.mcapSnapshots = td.mcapSnapshots.filter(s => now - s.time < 120000);

        // HOLDER GROWTH RATE — holders/min over last 30s
        const hSnap30 = td.holderSnapshots.filter(s => now - s.time < 30000);
        const holderGrowthRate = hSnap30.length >= 2 ?
          (hSnap30[hSnap30.length-1].count - hSnap30[0].count) / ((now - hSnap30[0].time) / 60000) : 0;

        // MCAP VELOCITY — $ change per minute over last 30s
        const mSnap30 = td.mcapSnapshots.filter(s => now - s.time < 30000);
        const mcapVelocity = mSnap30.length >= 2 ?
          (mSnap30[mSnap30.length-1].mcap - mSnap30[0].mcap) / ((now - mSnap30[0].time) / 60000) : 0;
        // MCAP trajectory as % change per minute
        const mcapStart = mSnap30.length >= 2 ? mSnap30[0].mcap : mcap;
        const mcapTrajectory = mcapStart > 0 ? (mcapVelocity / mcapStart) * 100 : 0;

        // 2. BUY VELOCITY — buys in last 30s vs previous 30s
        const recentBuys = td.buyTimes.filter(t2 => now - t2 < 30000).length;
        const prevBuys = td.buyTimes.filter(t2 => now - t2 >= 30000 && now - t2 < 60000).length;
        const velocity = recentBuys; // raw buys per 30s
        const accelerating = recentBuys > prevBuys * 1.3 && recentBuys >= 3;

        // 3. BUY SIZE DISTRIBUTION — lots of small buys = organic
        const smallBuys = td.buySizes.filter(s => s < 0.5).length; // under 0.5 SOL (~$42)
        const largeBuys = td.buySizes.filter(s => s > 3).length;   // over 3 SOL (~$250)
        const smallBuyRatio = td.buySizes.length > 0 ? smallBuys / td.buySizes.length : 0;
        const avgBuySize = td.buySizes.length > 0 ? td.buySizes.reduce((a, b) => a + b, 0) / td.buySizes.length : 0;

        // 4. TIME TO MILESTONES — faster = hotter
        const timeTo10k = td.milestones[10000] || 0;
        const timeTo20k = td.milestones[20000] || 0;
        const fastRunner = timeTo10k > 0 && timeTo10k < 120000; // hit 10K in under 2 min
        const rocketShip = timeTo20k > 0 && timeTo20k < 180000; // hit 20K in under 3 min

        // 5. HOLDER RETENTION — are early buyers holding or dumping?
        let earlyHolding = 0, earlyDumped = 0;
        Object.values(td.earlyBuyers).forEach(eb => {
          const net = eb.totalBought - eb.totalSold;
          if (net > eb.initBuy * 0.3) earlyHolding++;  // still holding 30%+
          else earlyDumped++;
        });
        const retentionPct = td.earlyBuyerCount > 0 ? (earlyHolding / td.earlyBuyerCount) * 100 : 50;

        // 6. DEPLOYER REPUTATION — graded A-F
        const depHist = deployerHistory.current[td.deployer];
        const depLaunches = depHist ? depHist.launches : 0;
        const depRugs = depHist ? depHist.rugs : 0;
        let deployerGrade = "A"; // no history = clean
        if (depRugs > 0) deployerGrade = depLaunches > 5 ? "F" : "D";
        else if (depLaunches > 8) deployerGrade = "F";
        else if (depLaunches > 3) deployerGrade = "C";
        else if (depLaunches > 1) deployerGrade = "B";
        const serialDeployer = depLaunches > 3;
        const hyperDeployer = depLaunches > 8;

        // 7. BUNDLE DETECTION — coordinated buys (live active count)
        const bundleDetected = !!td.bundleDetected;
        let bundleSize = 0;
        if (bundleDetected && td.bundleWallets) {
          td.bundleWallets.forEach(w => {
            const wd = td.wallets[w];
            if (wd && (wd.bought - wd.sold) > 0.01) bundleSize++;
          });
          td.bundleSize = bundleSize; // update live count
        }

        // 8. SMART MONEY — winning wallets still holding? (live active count)
        let hasSmartMoney = false;
        let smartWalletCount = 0;
        const smartSet = td.smartWallets || new Set();
        // Check tracked smart wallets + scan all wallets for new smart money
        // Also remove wallets that have degraded below threshold
        const newSmartSet = new Set();
        Object.keys(td.wallets).forEach(w => {
          const ws = walletScores.current[w];
          const wsTotal2 = ws ? (ws.wins + ws.losses) : 0;
          const wsRate2 = wsTotal2 > 0 ? ws.wins / wsTotal2 : 0;
          if (ws && ws.wins >= 3 && wsRate2 >= 0.60 && ws.wins > (ws.losses||0) && wsTotal2 >= 4) {
            newSmartSet.add(w);
            const wd = td.wallets[w];
            if (wd && (wd.bought - wd.sold) > 0.01) {
              hasSmartMoney = true;
              smartWalletCount++;
            }
          }
        });
        td.smartWallets = newSmartSet;

        // 9. NARRATIVE MATCH — is token name trending?
        let narrativeMatch = false;
        let narrativeWord = "";
        setNarratives(narr => {
          const nameWords = (t.name || "").toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/);
          for (const n of narr) {
            if (nameWords.includes(n.word) && n.count >= 3) {
              narrativeMatch = true;
              narrativeWord = n.word;
              break;
            }
          }
          return narr;
        });

        // 10. STALENESS — how long since last trade (migrated tokens get grace)
        const staleSec = Math.floor((now - td.lastTradeTime) / 1000);
        const migratedGrace = t.migrated && t.migratedAt && (now - t.migratedAt < 900000); // 15min grace — Raydium data needs time to populate
        const isStale = !migratedGrace && staleSec > 60;  // no trades in 60s
        const isDead = !migratedGrace && staleSec > 180;  // no trades in 3 min = probably dead

        // Track deployer rugs (mark once)
        if (isDead && td.deployer && !td._rugMarked) {
          td._rugMarked = true;
          const dh = deployerHistory.current[td.deployer];
          if (dh) dh.rugs = (dh.rugs || 0) + 1;
        }

        // 11. RECENT SELL PRESSURE — sells vs buys in last 30s
        const recentBuys30 = td.buyTimes.filter(t2 => now - t2 < 30000).length;
        const recentSells30 = td.sellTimes.filter(t2 => now - t2 < 30000).length;
        const sellDumping = recentSells30 > recentBuys30 * 2 && recentSells30 >= 3;

        // ═══════════ RISK SCORING (soft signals) ═══════════
        let risk = 40;

        // Base metrics
        if (buys > 5) risk += 8;
        if (buys > sells * 1.5 && buys > 3) risk += 8;
        if (volUsd > 1000) risk += 8;
        if (mcap > 5000) risk += 5;
        if (uniqueWallets > 10) risk += 8;
        if (topPct > 50) risk -= 12;
        if (td.deployerSold) risk -= 15;
        if (devPct > 30) risk -= 10;
        if (isSuspicious) risk -= 25;

        // Fresh wallet penalty (soft — not disqualifying)
        if (freshPct > 85 && td.totalBuyers > 5) risk -= 12;   // very sus
        else if (freshPct > 70 && td.totalBuyers > 8) risk -= 6; // kinda sus
        else if (freshPct < 50) risk += 5;                       // established wallets = good

        // Buy velocity bonus
        if (accelerating) risk += 10;
        if (velocity >= 5) risk += 5;   // 5+ buys in 30s = hot

        // Buy size distribution
        if (smallBuyRatio > 0.6 && td.buySizes.length > 5) risk += 8;  // lots of small = organic retail
        if (largeBuys > 3 && smallBuys < 2) risk -= 8;                 // all whales, no retail = propped up

        // Speed bonus
        if (fastRunner) risk += 8;
        if (rocketShip) risk += 5;

        // Holder retention
        if (retentionPct > 70 && td.earlyBuyerCount >= 5) risk += 10;  // diamond hands
        else if (retentionPct < 30 && td.earlyBuyerCount >= 5) risk -= 10; // early dump

        // Staleness — no trades = dying
        if (isDead) risk -= 25;       // 3+ min no trades = probably rugged
        else if (isStale) risk -= 12;  // 1+ min no trades = losing interest

        // Active sell pressure
        if (sellDumping) risk -= 15;  // sells overwhelming buys = dump in progress

        // Bundle detection — coordinated launch
        if (bundleDetected) risk -= 15;  // multi-wallet same-block buys = likely manipulated

        // Smart money signal
        if (hasSmartMoney) risk += 8;   // proven winners are buying
        if (smartWalletCount >= 3) risk += 5;  // multiple smart wallets = strong conviction

        // Narrative momentum
        if (narrativeMatch) risk += 5;   // riding a trending meta

        // Deployer grade (replaces simple serial/hyper check)
        if (deployerGrade === "F") risk -= 20;  // serial rugger
        else if (deployerGrade === "D") risk -= 15;  // has rugged before
        else if (deployerGrade === "C") risk -= 8;   // too many launches
        else if (deployerGrade === "A") risk += 3;    // clean deployer

        // On-chain flags from Helius
        if (t.mintAuth) risk -= 20;    // mint authority still enabled = can create more tokens
        if (t.frozen) risk -= 25;      // frozen = can freeze your tokens
        risk = Math.max(0, Math.min(100, risk));

        // ═══════════ QUALIFICATION CHECKS ═══════════
        let score = 0;
        const checks = [];
        const test = (n, pass) => { checks.push({ name: n, pass: !!pass }); if (pass) score++; };

        const isMig = t.migrated === true;
        test("RISK", risk >= 45);
        test("WALLETS", isMig ? (t.holders || uniqueWallets) >= 4 : uniqueWallets >= 4); // use Helius count for migrated
        test("DEV<25%", devPct < 25);
        test("NO DUMP", !td.deployerSold || td.deployerSellPct < 50);
        test("ORGANIC", !isSuspicious);
        test("VOLUME", isMig ? (t.vol || 0) > 200 : volUsd > 200); // use DexScreener vol for migrated
        test("BUYS", isMig ? true : (buys > sells && buys > 2)); // migrated already proved buys on pump
        test("DISTRO", topPct < 55);

        const qualified = isMig ? score >= 4 : score >= 5; // migrated gets slightly lower bar — already proven
        const threat = isSuspicious ? "SUSPICIOUS" :
          hyperDeployer ? "SERIAL DEV" :
          bundleDetected ? "⚠ BUNDLED" :
          td.deployerSold ? "DEV SOLD" :
          hasSmartMoney ? "🧠 SMART$" :
          accelerating && risk > 60 ? "🔥 HOT" :
          risk > 75 ? "LOW" : risk > 55 ? "MODERATE" : risk > 35 ? "HIGH" : "EXTREME";
        const threatColor = isSuspicious || hyperDeployer ? "#ff073a" :
          bundleDetected ? "#ff073a" :
          td.deployerSold ? "#ff073a" :
          hasSmartMoney ? "#ff9500" :
          accelerating && risk > 60 ? "#39ff14" :
          risk > 75 ? "#39ff14" : risk > 55 ? "#ffe600" : risk > 35 ? "#ff6600" : "#ff073a";

        // Fire events once
        if (!firedEvents.current.has(t.id)) {
          firedEvents.current.add(t.id);

          if (isSuspicious) {
            setIntelEvents(p => [{
              id: Date.now() + Math.random(), type: "rug_pattern", icon: "🔴", color: "#ff073a",
              text: `${t.name} — SUSPICIOUS: $${formatNum(mcap)} mcap, only $${formatNum(volUsd)} vol (${(volMcapRatio * 100).toFixed(1)}%)`,
              timestamp: Date.now(), priority: "HIGH",
            }, ...p].slice(0, 40));
          }

          if (serialDeployer) {
            setIntelEvents(p => [{
              id: Date.now() + Math.random(), type: "rug_pattern", icon: "⚠", color: "#ff6600",
              text: `${t.name} — SERIAL DEPLOYER: ${depHist.launches} tokens launched from this wallet`,
              timestamp: Date.now(), priority: "HIGH",
            }, ...p].slice(0, 40));
          }

          if (freshPct > 80 && td.totalBuyers > 5) {
            setIntelEvents(p => [{
              id: Date.now() + Math.random(), type: "rug_pattern", icon: "👻", color: "#ff6600",
              text: `${t.name} — ${Math.round(freshPct)}% fresh wallets (${td.freshBuyers}/${td.totalBuyers}) — possible coordinated buy`,
              timestamp: Date.now(), priority: "NORMAL",
            }, ...p].slice(0, 40));
          }

          if (rocketShip) {
            setIntelEvents(p => [{
              id: Date.now() + Math.random(), type: "momentum", icon: "⚡", color: "#ffd740",
              text: `${t.name} — ROCKET: hit $20K in ${(timeTo20k / 1000).toFixed(0)}s`,
              timestamp: Date.now(), priority: "HIGH",
            }, ...p].slice(0, 40));
          }

          if (qualified) {
            setStats(s => ({ ...s, deployed: s.deployed + 1 }));
            setIntelEvents(p => [{
              id: Date.now() + Math.random(), type: "deploy", icon: "⚡", color: "#39ff14",
              text: `${t.name} qualified ${score}/8 — ${uniqueWallets}w, $${formatNum(volUsd)} vol, ${Math.round(freshPct)}% fresh, ${Math.round(retentionPct)}% retain`,
              timestamp: Date.now(), priority: "HIGH",
            }, ...p].slice(0, 40));
          } else {
            setStats(s => ({ ...s, rejected: s.rejected + 1 }));
            setIntelEvents(p => [{
              id: Date.now() + Math.random(), type: "reject", icon: "🚫", color: "#ff073a",
              text: `${t.name} failed ${score}/8 — ${threat}`,
              timestamp: Date.now(), priority: "NORMAL",
            }, ...p].slice(0, 40));
          }
        }

        // Migrated tokens: don't overwrite mcap/vol/holders from PumpFun tradeData
        // These are kept accurate by the migratedInterval (DexScreener + Helius)
        const isMigratedToken = t.migrated === true;
        const updatedToken = {
          ...t, qualified, qualScore: score, qualChecks: checks,
          riskScore: risk, threat, threatColor,
          mcap: isMigratedToken ? t.mcap : (mcap > 0 ? mcap : t.mcap),
          vol: isMigratedToken ? t.vol : volUsd,
          buys: isMigratedToken ? t.buys : buys,
          sells: isMigratedToken ? t.sells : sells,
          holders: isMigratedToken ? t.holders : uniqueWallets, // Helius count preserved for migrated
          topHolderPct: Math.round(topPct),
          devWallet: Math.round(devPct),
          health: qualified ? 70 + (risk - 50) * 0.6 : 20 + risk * 0.3,
          freshPct: Math.round(freshPct),
          velocity, accelerating,
          smallBuyRatio: Math.round(smallBuyRatio * 100),
          avgBuySol: avgBuySize,
          retentionPct: Math.round(retentionPct),
          timeTo10k, timeTo20k, fastRunner, rocketShip,
          holderGrowthRate: Math.round(holderGrowthRate * 10) / 10,
          mcapVelocity: Math.round(mcapVelocity),
          mcapTrajectory: Math.round(mcapTrajectory * 10) / 10,
          serialDeployer, deployerLaunches: depHist ? depHist.launches : 0,
          staleSec, isStale, isDead, sellDumping,
          bundleDetected, bundleSize,
          hasSmartMoney, smartWalletCount,
          narrativeMatch, narrativeWord,
          deployerGrade,
          peakMcap: Math.max(t.peakMcap || 0, mcap > 0 ? mcap : t.mcap),
        };
        // Write to DB when newly qualified or when mcap hits new peak
        if ((qualified && !t.qualified) || (updatedToken.peakMcap > (t.peakMcap || 0))) {
          sbUpsertToken(updatedToken);
        }
        return updatedToken;
      });
      return result;
      });
    }, 5000);

    // ─── ENRICHMENT: DexScreener BATCH ALL + Helius metadata ───
    // Batch fetches ALL qualified tokens at once vs old 3-per-10s approach
    const enriched = {};  // { mint: { lastHelius: ms, heliusDone: bool } }
    const dexInterval = setInterval(async () => {
      const now = Date.now();

      // ── DEXSCREENER BATCH: ALL qualified alive tokens in one call ──
      const allQualified = tokensRef.current
        .filter(t => t.qualified && t.addr && t.alive)
        .map(t => t.addr);

      if (allQualified.length > 0) {
        try {
          const results = await fetchTokensBatch(allQualified); // up to 30 per call
          const updatedAddrs = Object.keys(results);

          if (updatedAddrs.length > 0) {
            // Flash snap + mcap calibration for all returned tokens
            updatedAddrs.forEach(addr => {
              const dex = results[addr];
              if (!dex?.mcap || dex.mcap <= 0) return;
              // Flash snapshots for 30s/1m boards
              if (!flashSnapsRef.current[addr]) flashSnapsRef.current[addr] = [];
              flashSnapsRef.current[addr].push({ time: now, mcap: dex.mcap });
              flashSnapsRef.current[addr] = flashSnapsRef.current[addr].filter(s => now - s.time < 90000);
              // Auto-calibrate MCAP_CORRECTION
              const td = tradeData.current[addr];
              if (td) {
                if (td.mcapSource === "pp" && td.lastMcapSol > 0) {
                  const ppMcapUsd = td.lastMcapSol * SOL_USD;
                  const ratio = dex.mcap / ppMcapUsd;
                  if (ratio > 0.1 && ratio < 2.0) {
                    mcapSamples.push(ratio);
                    if (mcapSamples.length > 30) mcapSamples.shift();
                    const sorted = [...mcapSamples].sort((a, b) => a - b);
                    const newCorr = sorted[Math.floor(sorted.length / 2)];
                    if (Math.abs(newCorr - MCAP_CORRECTION) > 0.02) {
                      MCAP_CORRECTION = newCorr;
                      console.log(`[MCAP] 🔧 Correction: ${MCAP_CORRECTION.toFixed(3)}`);
                    }
                  }
                }
                td.lastMcapSol = dex.mcap / SOL_USD;
                td.mcapSource = "dex";
              }
            });

            // Batch update — pre-mig tokens: metadata only (pump socket owns mcap/vol/buys/sells)
            // Migrated tokens: full update handled by migratedInterval, dexInterval only fills gaps
            setTokens(prev => {
              let changed = false;
              const updated = prev.map(t => {
                const dex = results[t.addr];
                if (!dex) return t;
                changed = true;
                if (t.migrated) {
                  // Post-mig: dexInterval fills gaps only — migratedInterval is the primary source
                  return {
                    ...t,
                    imageUri: dex.imageUrl || t.imageUri,
                    priceChange5m: dex.priceChange5m !== 0 ? dex.priceChange5m : t.priceChange5m,
                    priceChange1h: dex.priceChange1h !== 0 ? dex.priceChange1h : t.priceChange1h,
                    dexEnriched: true,
                  };
                }
                // Pre-mig: pump socket owns mcap/vol/buys/sells — dex only adds metadata
                return {
                  ...t,
                  imageUri: dex.imageUrl || t.imageUri,
                  priceChange5m: dex.priceChange5m !== 0 ? dex.priceChange5m : t.priceChange5m,
                  priceChange1h: dex.priceChange1h !== 0 ? dex.priceChange1h : t.priceChange1h,
                  // Only use dex mcap if pump socket hasn't given us one yet
                  mcap: (t.mcap && t.mcap > 0) ? t.mcap : (dex.mcap > 0 ? dex.mcap : t.mcap),
                  liquidity: dex.liquidity || t.liquidity,
                  dexEnriched: true,
                };
              });
              return changed ? updated : prev;
            });
            console.log(`[DEX] ⚡ BRAIN PULSE — ${updatedAddrs.length}/${allQualified.length} tokens refreshed`);
          }
        } catch (e) {
          console.warn('[DEX] Batch failed:', e.message);
        }
      }

      // ── HELIUS: on-chain metadata, 3 per cycle, retry every 30s ──
      const heliusBatch = tokensRef.current
        .filter(t => t.qualified && t.addr)
        .filter(t => {
          const e = enriched[t.addr];
          if (!e?.heliusDone) return true; // never fetched
          if (now - (e.lastHelius || 0) > 30000) return true; // stale
          if (t.migrated && (t.holders || 0) <= 1) return true; // migrated with bad holder count — keep retrying
          return false;
        })
        .slice(0, 3)
        .map(t => t.addr);

      for (const mint of heliusBatch) {
        if (!enriched[mint]) enriched[mint] = { lastHelius: 0, heliusDone: false };
        try {
          const [meta, largest, count] = await Promise.all([
            fetchTokenMeta(mint),
            fetchLargestHolders(mint),
            fetchHolderCount(mint),
          ]);
          enriched[mint].lastHelius = now;
          enriched[mint].holderRetries = (enriched[mint].holderRetries || 0) + 1;
          // Fall back to public RPC if primary returned 0 or 1 (DAS holderCount is bugged for pumpswap — skip it)
          var realHolders = count > 1 ? count : (await fetchHolderCountPublic(mint)) || count || 0;
          // Only lock out retries if we got real holder data (>1), or exhausted retries
          enriched[mint].heliusDone = realHolders > 1 || enriched[mint].holderRetries >= 5;
          if (meta || realHolders > 0) {
            setTokens(prev => prev.map(t => {
              if (t.addr !== mint) return t;
              return {
                ...t,
                mintAuth: meta?.mintAuth || false,
                frozen: meta?.frozen || false,
                holders: realHolders > 1 ? realHolders : t.holders,
                topHolderPct: largest?.topHolderPct > 0 ? Math.round(largest.topHolderPct) : t.topHolderPct,
                heliusEnriched: true,
              };
            }));
            console.log(`[HELIUS] ✅ ${mint.slice(0, 8)} — holders:${realHolders} top10:${largest?.topHolderPct?.toFixed(1)}%`);
          }
        } catch (e) {
          enriched[mint].heliusDone = true;
          enriched[mint].lastHelius = now;
          console.warn(`[HELIUS] ❌ ${mint.slice(0, 8)}`, e.message);
        }
      }
    }, 8000);

    // ─── SOL PRICE: DexScreener only (Jupiter API deprecated/auth required) ───
    const SOL_MINT = "So11111111111111111111111111111111111111112";
    const fetchSolPrice = async () => {
      try {
        const res = await fetch("https://api.dexscreener.com/tokens/v1/solana/So11111111111111111111111111111111111111112");
        const data = await res.json();
        const pair = data?.pairs?.[0] || (Array.isArray(data) ? data[0] : null);
        if (pair && pair.priceUsd) {
          const newPrice = parseFloat(pair.priceUsd);
          if (newPrice > 10 && newPrice < 1000) {
            SOL_USD = newPrice;
            return;
          }
        }
      } catch (e) { console.warn("[SOL] DexScreener failed:", e.message); }
    };
    fetchSolPrice(); // immediate
    const solPriceInterval = setInterval(fetchSolPrice, 30000); // every 30s

    // ─── FALLBACK MIGRATION DETECTION: catch tokens PumpPortal missed ───
    // If a token has high mcap and goes silent, check DexScreener for Raydium pair
    const migFallbackChecked = new Set();
    const migFallbackInterval = setInterval(async () => {
      const candidates = [];
      const now = Date.now();
      Object.entries(tradeData.current).forEach(([mint, td]) => {
        if (migratedMints.current.has(mint)) return; // already migrated
        if (migFallbackChecked.has(mint)) return; // already checked and failed
        if (!td.lastMcapSol || td.lastMcapSol < 300) return; // not near migration
        const silentSec = (now - td.lastTradeTime) / 1000;
        if (silentSec < 10) return; // still getting trades
        if (silentSec > 300) return; // too old, probably dead
        candidates.push({ mint, mcapSol: td.lastMcapSol, silentSec });
      });
      if (candidates.length === 0) return;
      // Check top 2 candidates
      const toCheck = candidates.sort((a, b) => b.mcapSol - a.mcapSol).slice(0, 2);
      for (const c of toCheck) {
        try {
          const dex = await fetchTokenByAddress(c.mint);
          if (!dex) { migFallbackChecked.add(c.mint); continue; }
          const isRaydium = (dex.platform || "").toLowerCase().includes("raydium");
          const highMcap = (dex.mcap || 0) > 25000;
          if (isRaydium || highMcap) {
            console.log(`[MIGRATE-FALLBACK] 🌉 Detected ${c.mint.slice(0,8)} on ${dex.platform} — $${formatNum(dex.mcap)} mcap (was silent ${c.silentSec.toFixed(0)}s)`);
            // Trigger migration
            migratedMints.current.add(c.mint);
            const tokenId = mintToId.current[c.mint];
            const td = tradeData.current[c.mint];
            let tokenName = c.mint.slice(0, 8);
            const tok = tokensRef.current.find(t => t.id === tokenId);
            if (tok) tokenName = tok.name;
            const mcapUsd = dex.mcap || (c.mcapSol * SOL_USD);
            setMigrations(prev => {
              if(prev.some(m=>m.mint===c.mint)) return prev;
              return [{
              id: Date.now() + Math.random(), mint: c.mint, tokenId, name: tokenName,
              mcap: mcapUsd, vol: dex.vol || (td ? td.volSol * SOL_USD : 0),
              holders: td ? Object.keys(td.wallets || {}).length : 0,
              timestamp: Date.now(), laserActive: true,
            }, ...prev].slice(0, 20)});
            if (tokenId) {
              const mc2 = mcapUsd;
              const zz=[[5000,0.95],[10000,0.63],[20000,0.47],[50000,0.32],[100000,0.20],[300000,0.10]];
              let entryY=0.95;
              if(mc2>=300000)entryY=0.05;
              else{for(let i=0;i<zz.length-1;i++){
                if(mc2>=zz[i][0]&&mc2<zz[i+1][0]){
                  const pct=(mc2-zz[i][0])/(zz[i+1][0]-zz[i][0]);
                  entryY=zz[i][1]+(zz[i+1][1]-zz[i][1])*pct;break;}}}
              const landX = 0.3 + Math.random() * 0.35;
              setTokens(prev => prev.map(t => t.id === tokenId && !t.migrated ? {
                ...t, migrated: true, migratedAt: Date.now(),
                laserIn: true, laserProgress: 0,
                laserTargetX: landX, laserTargetY: entryY,
                bx: 1.02, by: 0.18, targetY: entryY,
                warpIn: false, alive: true, qualified: true, health: 95,
                mcap: mcapUsd,
              } : t));
            }
            // Update tradeData
            if (td) {
              td.lastTradeTime = Date.now();
              td.lastMcapSol = (dex.mcap || 0) / SOL_USD;
              td.mcapSource = "dex";
            }
            setIntelEvents(prev => [{
              id: Date.now() + Math.random(), type: "migration", icon: "🌉", color: "#39ff14",
              text: `${tokenName} MIGRATED (fallback detected) — $${formatNum(mcapUsd)} on ${dex.platform}`,
              timestamp: Date.now(), priority: "HIGH",
            }, ...prev].slice(0, 40));
          } else {
            migFallbackChecked.add(c.mint);
          }
        } catch (e) {
          console.warn("[MIGRATE-FALLBACK] error:", e.message);
        }
      }
    }, 8000); // check every 8s

    // ─── HELIUS WEBSOCKET: Real-time post-migration swap detection ───
    const PUMP_SUPPLY = 1_000_000_000;
    let lastWsPrice = {}; // debounce: {mint: timestamp}

    const heliusWs = connectHeliusWS(async (activity) => {
      const { mint, isSwap, timestamp } = activity;
      if (!isSwap) return; // only care about swaps

      // Debounce: max 1 price fetch per mint per 2s
      if (lastWsPrice[mint] && timestamp - lastWsPrice[mint] < 2000) return;
      lastWsPrice[mint] = timestamp;

      const id = mintToId.current[mint];
      if (!id) return;

      // Instant DexScreener price fetch on swap event
      try {
        const dex = await fetchTokenByAddress(mint);
        if (!dex || !dex.mcap) return;
        const jupMcap = dex.mcap;
        const priceUsd = dex.priceUsd || 0;

        const td = tradeData.current[mint];
        if (td) { td.lastTradeTime = Date.now(); td.lastMcapSol = jupMcap / SOL_USD; }

        setTokens(prev => prev.map(t => t.id === id ? {
          ...t, mcap: jupMcap, priceUsd,
          health: jupMcap > 10000 ? Math.max(t.health, 50) : t.health,
        } : t));

        setMigrations(prev => prev.map(m => m.mint === mint ? {
          ...m, curMcap: jupMcap, curPrice: priceUsd, lastDexUpdate: Date.now(),
        } : m));
      } catch (e) {
        console.warn("[HELIUS-WS] DexScreener fetch failed:", e.message);
      }
    });
    heliusWsRef.current = heliusWs;

    // Subscribe existing migrated mints (in case of reconnect)
    if (heliusWs) {
      migratedMints.current.forEach(mint => heliusWs.subscribe(mint));
    }

    // ─── MIGRATED TOKEN FALLBACK POLLING: DexScreener for vol/buys/sells + Helius holders ───
    let migratedLastSeen = {};
    let migratedHolderCycle = 0;

    // === BATCH 2s poll: all migrated tokens in ONE DexScreener request ===
    const migratedInterval = setInterval(async () => {
      const curTokens = tokensRef.current;
      const now = Date.now();

      // Scan tokensRef directly — catches DB-loaded migrated tokens that were never in migratedMints
      const toPoll = curTokens
        .filter(t => t.migrated && t.alive && t.addr)
        .map(t => ({ addr: t.addr, id: t.id }));

      // Also keep migratedMints in sync for other consumers
      toPoll.forEach(mt => { migratedMints.current.add(mt.addr); });

      if (toPoll.length === 0) return;

      // One batch request for ALL migrated tokens — much cheaper than N sequential calls
      try {
        const results = await fetchTokensBatch(toPoll.map(m => m.addr));
        toPoll.forEach(mt => {
          const dex = results[mt.addr];
          if (!dex) return;
          const td = tradeData.current[mt.addr];
          if (td) {
            // Only bump lastTradeTime if there's actual new trading activity
            const prevActivity = (td.buys || 0) + (td.sells || 0);
            if (dex.buys >= 0) td.buys = dex.buys;
            if (dex.sells >= 0) td.sells = dex.sells;
            const newActivity = (td.buys || 0) + (td.sells || 0);
            if (newActivity > prevActivity) td.lastTradeTime = now;
            if (dex.mcap) { td.lastMcapSol = dex.mcap / SOL_USD; td.mcapSource = "dex"; }
          }
          setTokens(prev => prev.map(t => t.id === mt.id ? {
            ...t,
            mcap: dex.mcap || t.mcap,
            vol: dex.vol != null ? dex.vol : t.vol,
            buys: dex.buys != null ? dex.buys : t.buys,
            sells: dex.sells != null ? dex.sells : t.sells,
            liquidity: dex.liquidity != null ? dex.liquidity : t.liquidity,
            dexEnriched: true,
          } : t));
          setMigrations(prev => prev.map(m => m.mint === mt.addr ? {
            ...m,
            curMcap: dex.mcap || m.curMcap,
            curVol: dex.vol || m.curVol,
            curBuys: dex.buys != null ? dex.buys : m.curBuys,
            curSells: dex.sells != null ? dex.sells : m.curSells,
            curLiquidity: dex.liquidity || m.curLiquidity,
            lastDexUpdate: now,
          } : m));
        });
      } catch(e) { console.warn("[MIGRATED-BATCH]", e.message); }

      // Kill migrated tokens that have lost steam: mcap < $3K, or silent 8min post-grace
      toPoll.forEach(mt => {
        const t = tokensRef.current.find(r => r.id === mt.id);
        if (!t || !t.alive || !t.migrated) return;
        const peak = t.peakMcap || t.mcap || 0;
        const cur = t.mcap || 0;
        const migratedAge = now - (t.migratedAt || now);
        const grace = migratedAge < 600000; // 10 min grace after migration
        if (grace) return;
        const staleSec = (now - (tradeData.current[mt.addr]?.lastTradeTime || t.migratedAt || now)) / 1000;
        const collapsed = cur < 3000 || (peak > 10000 && cur < peak * 0.05);
        const prolongedSilence = staleSec > 480; // 8min no trades post-grace
        if (collapsed || prolongedSilence) {
          console.log(`[MIGRATED] 💀 ${t.name} dying — ${collapsed ? `collapsed $${cur.toFixed(0)}` : `silent ${staleSec.toFixed(0)}s`}`);
          setTokens(prev => prev.map(tok => tok.id === mt.id ? { ...tok, alive: false, health: 0, deathTime: now } : tok));
          migratedMints.current.delete(mt.addr);
        }
      });

      // Holder refresh — round-robin ALL migrated tokens, 1 per 2s cycle, 30s cooldown per token
      migratedHolderCycle++;
      if (toPoll.length > 0) {
        const mt = toPoll[migratedHolderCycle % toPoll.length];
        if (mt) {
          const lastHolderFetch = migratedLastSeen[mt.addr + '_hf'] || 0;
          if (now - lastHolderFetch >= 30000) { // max once per 30s per token
            migratedLastSeen[mt.addr + '_hf'] = now;
            try {
              // Try Helius first, fall back to public RPC if no key or returns 0
              var holderCount = await fetchHolderCount(mt.addr);
              if (holderCount <= 1) holderCount = (await fetchHolderCountPublic(mt.addr)) || holderCount;
              const largest = await fetchLargestHolders(mt.addr);
              const topPct = largest?.topHolderPct || 0;
              if (holderCount > 1) {
                console.log(`[HOLDERS] ✅ ${mt.addr.slice(0,8)}: ${holderCount}`);
                setTokens(prev => prev.map(t => t.id === mt.id ? {
                  ...t, holders: holderCount, topHolderPct: topPct,
                } : t));
                setMigrations(prev => prev.map(m => m.mint === mt.addr ? {
                  ...m, curHolders: holderCount, lastDexUpdate: now,
                } : m));
              } else {
                console.warn(`[HOLDERS] ⚠ ${mt.addr.slice(0,8)}: all sources returned ${holderCount} (need >1)`);
              }
            } catch(e) {
              console.warn(`[HOLDERS] ❌ ${mt.addr.slice(0,8)}:`, e.message);
            }
          }
        }
      }
    }, 2000);

    // Bay audit removed — inHoldingBay tokens released by App.jsx timer

    return () => {
      if (typeof pf === 'function') pf();
      else if (pf && pf.cleanup) pf.cleanup();
      if (heliusWs) heliusWs.cleanup();
      clearInterval(qualInterval);
      clearInterval(dexInterval);
      // jupInterval removed - Jupiter API deprecated
      clearInterval(solPriceInterval);
      clearInterval(migratedInterval);
      clearInterval(migFallbackInterval);

    };
  }, []);

  // ═══ DEXSCREENER POLLING — catches Moonshot, Bags, etc ═══
  const dexSeenRef = useRef(new Set());
  useEffect(() => {
    const pollDex = async () => {
      try {
        const [profiles, boosts] = await Promise.all([
          fetchLatestProfiles(),
          fetchBoostedTokens(),
        ]);
        // Combine and dedupe
        const allTokens = [...profiles, ...boosts];
        const newMints = [];
        allTokens.forEach(t => {
          const addr = t.tokenAddress;
          if (!addr || dexSeenRef.current.has(addr) || mintToId.current[addr]) return;
          dexSeenRef.current.add(addr);
          newMints.push(addr);
        });
        if (newMints.length === 0) return;

        // Fetch full pair data for new tokens (batch up to 5 at a time)
        const batch = newMints.slice(0, 5);
        for (const mint of batch) {
          try {
            const res = await fetch(`https://api.dexscreener.com/tokens/v1/solana/${mint}`);
            const data = await res.json();
            const pair = data?.pairs?.[0] || (Array.isArray(data) ? data[0] : null);
            if (!pair || !pair.baseToken) continue;

            const mcap = pair.marketCap || pair.fdv || 0;
            if (mcap < 3200 || mcap > 500000) continue; // filter to our range

            const name = pair.baseToken.symbol || "???";
            const dexId = (pair.dexId || "").toLowerCase();
            let platform = "DexScreener";
            if (dexId.includes("moonshot") || dexId.includes("moonit")) platform = "Moonshot";
            else if (dexId.includes("raydium") && pair.labels?.includes("Bonk")) platform = "Bonk";
            else if (dexId.includes("raydium")) platform = "Raydium";
            else if (dexId.includes("orca")) platform = "Orca";
            else if (dexId.includes("meteora")) platform = "Meteora";

            const id = mint + Date.now();
            mintToId.current[mint] = id;

            // Lightweight tradeData init
            tradeData.current[mint] = {
              buys: pair.txns?.h1?.buys || 0, sells: pair.txns?.h1?.sells || 0,
              volSol: 0, lastMcapSol: mcap / SOL_USD, mcapSource: "dex",
              deployer: "", wallets: {}, deployerSold: false, deployerSellPct: 0,
              totalBoughtSol: 0, totalSoldSol: 0,
              freshBuyers: 0, totalBuyers: 0, buyTimes: [], buySizes: [],
              launchTime: pair.pairCreatedAt || Date.now(),
              lastTradeTime: Date.now(), sellTimes: [],
              milestones: {}, earlyBuyers: {}, earlyBuyerCount: 0,
            };

            const token = {
              id, addr: mint, name, fullName: pair.baseToken.name || name,
              platform, mcap, vol: pair.volume?.h24 || pair.volume?.h1 || 0,
              priceUsd: parseFloat(pair.priceUsd || 0), liquidity: pair.liquidity?.usd || 0,
              holders: 1, devWallet: 0,
              buys: pair.txns?.h1?.buys || 0, sells: pair.txns?.h1?.sells || 0,
              riskScore: 50, qualified: false, qualScore: 0, qualChecks: [],
              threat: "SCANNING", threatColor: "#ffe600",
              bundleDetected: false, bundleSize: 0, mintAuth: false,
              topHolderPct: 0, dupeCount: 0,
              bx: Math.random() * 0.8 + 0.1, by: 0.92, targetY: 0.92,
              vx: (Math.random() - 0.5) * 0.0006, health: 60, alive: true,
              age: 0, trail: [], warpIn: false, warpProgress: 1,
              warpStartX: Math.random(), warpStartY: Math.random() * 0.2 - 0.1,
              bobOffset: Math.random() * Math.PI * 2,
              initials: name.slice(0, 2).toUpperCase(),
              coinColor: pick(_LIVE_COIN_COLORS),
              timestamp: Date.now(), deployer: "",
              imageUri: pair.info?.imageUrl || "",
            };

            setTokens(prev => {
              let list = [token, ...prev];
              const now2 = Date.now();
              list = list.filter(t => {
                if (t.migrated) return true;
                if (!t.alive && now2 - (t.deathTime || t.timestamp) > 45000) return false;
                if (t.alive && !t.qualified && t.buys <= 0 && t.sells <= 0 && now2 - t.timestamp > 30000) return false;
                if (t.alive && !t.migrated && (t.mcap || 0) < 3000 && (t.buys || 0) < 3 && now2 - t.timestamp > 90000) return false;
                return true;
              });
              return list.slice(0, 150);
            });
            setStats(s => ({ ...s, scanned: s.scanned + 1 }));
            setIntelEvents(prev => [{
              id: Date.now() + Math.random(), type: "launch", icon: "🌐", color: "#ff9500",
              text: `[${platform}] ${name} spotted | $${mcap > 1000 ? (mcap/1000).toFixed(1)+"K" : mcap.toFixed(0)}`,
              timestamp: Date.now(), priority: "NORMAL",
            }, ...prev].slice(0, 40));
            console.log(`[DexScreener] Added ${name} from ${platform} — $${mcap.toFixed(0)}`);
          } catch (e) {
            console.warn("[DexScreener] Token fetch failed:", e.message);
          }
        }
      } catch (e) {
        console.warn("[DexScreener] Poll failed:", e.message);
      }
    };
    // Poll every 45 seconds
    const iv = setInterval(pollDex, 45000);
    setTimeout(pollDex, 10000); // first poll after 10s
    return () => clearInterval(iv);
  }, []);

  // ═══ CROSS-SOURCE ENRICHMENT — GeckoTerminal + Defined.fi + SolanaTracker ═══

  // ─── GECKO + DEFINED TRENDING: cross-reference our tokens with what's hot ───
  useEffect(() => {
    const pollTrending = async () => {
      try {
        const [geckoTrending, definedTrending] = await Promise.all([
          fetchGeckoTrending(),
          fetchDefinedTrending(),
        ]);

        // Store trending addresses for cross-referencing
        geckoTrendingRef.current = geckoTrending;
        definedTrendingRef.current = definedTrending;

        const geckoNames = new Set(geckoTrending.map(g => g.name?.split("/")?.[0]?.trim()?.toUpperCase()).filter(Boolean));
        const definedAddrs = new Set(definedTrending.map(d => d.address).filter(Boolean));
        const definedNames = new Set(definedTrending.map(d => d.symbol?.toUpperCase()).filter(Boolean));

        // Tag our tokens with trending status from multiple sources
        setTokens(prev => {
          let changed = false;
          const updated = prev.map(t => {
            const onGecko = geckoNames.has(t.name?.toUpperCase());
            const onDefined = definedAddrs.has(t.addr) || definedNames.has(t.name?.toUpperCase());
            const trendScore = (onGecko ? 1 : 0) + (onDefined ? 1 : 0) + (t.narrativeMatch ? 1 : 0);
            
            if (t.onGeckoTrending !== onGecko || t.onDefinedTrending !== onDefined || t.trendScore !== trendScore) {
              changed = true;
              return { ...t, onGeckoTrending: onGecko, onDefinedTrending: onDefined, trendScore };
            }
            return t;
          });
          return changed ? updated : prev;
        });

        // Fire intel events for newly trending tokens
        setTokens(prev => {
          var changed = false;
          var updated = prev.map(t => {
            var needsUpdate = false;
            if (t.onGeckoTrending && !t._geckoAnnounced) {
              needsUpdate = true;
              setIntelEvents(ev => [{
                id: Date.now() + Math.random(), type: "trend", icon: "🦎", color: "#39ff14",
                text: `${t.name} trending on GeckoTerminal! Cross-platform buzz detected.`,
                timestamp: Date.now(), priority: "HIGH",
              }, ...ev].slice(0, 40));
            }
            if (t.onDefinedTrending && !t._definedAnnounced) {
              needsUpdate = true;
              setIntelEvents(ev => [{
                id: Date.now() + Math.random(), type: "trend", icon: "📡", color: "#00ccff",
                text: `${t.name} flagged by Defined.fi — high social activity.`,
                timestamp: Date.now(), priority: "NORMAL",
              }, ...ev].slice(0, 40));
            }
            if (needsUpdate) {
              changed = true;
              return { ...t, _geckoAnnounced: t.onGeckoTrending || t._geckoAnnounced, _definedAnnounced: t.onDefinedTrending || t._definedAnnounced };
            }
            return t;
          });
          return changed ? updated : prev;
        });

        console.log(`[TRENDING] 🦎 Gecko: ${geckoTrending.length} pools | 📡 Defined: ${definedTrending.length} tokens`);
      } catch (e) {
        console.warn("[TRENDING] Poll failed:", e.message);
      }
    };
    const iv = setInterval(pollTrending, 60000); // every 60s
    setTimeout(pollTrending, 15000); // first poll after 15s
    return () => clearInterval(iv);
  }, []);

  var NEON_GREEN = "#39ff14";

  // ─── SOLANATRACKER: Bonding curve progress for pre-migration PumpFun tokens ───
  useEffect(() => {
    let cycle = 0;
    const iv = setInterval(async () => {
      cycle++;
      const cur = tokensRef.current;
      // Get qualified tokens — including migrated (SolanaTracker returns real holder count for both)
      const pumpTokens = cur.filter(t =>
        t.alive && t.qualified
      );
      // Sort: migrated tokens first (need holders most urgently), then by recency
      pumpTokens.sort((a, b) => (b.migrated ? 1 : 0) - (a.migrated ? 1 : 0));
      const pumpBatch = pumpTokens.slice(0, 8); // max 8 per cycle (was 4)

      for (const t of pumpBatch) {
        // Skip if cached less than 15s ago
        const cached = curveProgressCache.current[t.addr];
        if (cached && Date.now() - cached.ts < 15000) continue;

        try {
          const st = await fetchPumpCurveProgress(t.addr);
          if (!st) continue;
          curveProgressCache.current[t.addr] = { ...st, ts: Date.now() };

          setTokens(prev => prev.map(tok => tok.addr === t.addr ? {
            ...tok,
            bondingPct: tok.realSolReserves ? tok.bondingPct : st.bondingCurvePct,
            stVol1h: st.vol1h,
            stPriceChange5m: st.priceChange5m,
            stPriceChange1h: st.priceChange1h,
            holders: st.holders > 1 ? st.holders : tok.holders,
            stLiquidity: st.liquidityUsd,
          } : tok));

          // Persist real holder count to Supabase so leaderboard stays accurate
          if (st.holders > 1 && onUpsertTokenRef.current) {
            sbUpsertToken({ addr: t.addr, name: t.name, holders: st.holders });
          }

          // Alert when token is close to migration (>80% bonding)
          var effectivePct = t.realSolReserves ? t.bondingPct : st.bondingCurvePct;
          if (effectivePct != null && effectivePct > 80 && !bondingAlerted.current.has(t.addr)) {
            bondingAlerted.current.add(t.addr);
            setIntelEvents(ev => [{
              id: Date.now() + Math.random(), type: "migrate", icon: "🔥", color: NEON_GREEN,
              text: `${t.name} at ${effectivePct.toFixed(0)}% bonding — MIGRATION IMMINENT!`,
              timestamp: Date.now(), priority: "HIGH",
            }, ...ev].slice(0, 40));
          }

          if (st.bondingCurvePct != null) {
            console.log(`[CURVE] 📈 ${t.name} → ${st.bondingCurvePct.toFixed(1)}% to migration | $${(st.mcapUsd/1000).toFixed(1)}K mcap`);
          }
        } catch (e) {
          console.warn("[CURVE] Failed:", e.message);
        }
      }

      // ─── GECKO POOL ENRICHMENT — rotating window covers ALL migrated tokens ───
      if (cycle % 2 === 0) {
        const allMig = cur.filter(t => t.alive && t.migrated);
        const batchSize = 5;
        const offset = Math.floor(cycle / 2) * batchSize % Math.max(1, allMig.length);
        const migrated = allMig.length <= batchSize
          ? allMig
          : [...allMig.slice(offset, offset + batchSize),
             ...allMig.slice(0, Math.max(0, (offset + batchSize) - allMig.length))].slice(0, batchSize);
        for (const t of migrated) {
          try {
            const gp = await fetchGeckoPoolByToken(t.addr);
            if (!gp) continue;
            setTokens(prev => prev.map(tok => tok.addr === t.addr ? {
              ...tok,
              geckoMcap: gp.mcap,
              geckoVol5m: gp.vol5m,
              geckoVol1h: gp.vol1h,
              geckoVol24h: gp.vol24h,
              geckoBuys5m: gp.buys5m,
              geckoSells5m: gp.sells5m,
              geckoBuys1h: gp.buys1h,
              geckoSells1h: gp.sells1h,
              geckoChange5m: gp.priceChange5m,
              geckoChange1h: gp.priceChange1h,
              geckoReserve: gp.reserveUsd,
              geckoPoolAddr: gp.poolAddr,
              geckoDex: gp.dex,
            } : tok));

            // Also update migrations list
            setMigrations(prev => prev.map(m => m.mint === t.addr ? {
              ...m,
              geckoVol5m: gp.vol5m, geckoVol1h: gp.vol1h,
              geckoBuys5m: gp.buys5m, geckoSells5m: gp.sells5m,
              geckoChange5m: gp.priceChange5m, geckoChange1h: gp.priceChange1h,
              geckoReserve: gp.reserveUsd,
            } : m));

            console.log(`[GECKO] 🦎 ${t.name} → 5m: ${gp.priceChange5m>0?"+":""}${gp.priceChange5m.toFixed(1)}% (${gp.buys5m}b/${gp.sells5m}s) | 1h: ${gp.priceChange1h>0?"+":""}${gp.priceChange1h.toFixed(1)}% | reserve: $${(gp.reserveUsd/1000).toFixed(1)}K`);
          } catch (e) {
            console.warn("[GECKO] Pool enrichment failed:", e.message);
          }
        }
      }
    }, 10000); // every 10s
    return () => clearInterval(iv);
  }, []);

  // ─── JUPITER VERIFIED LIST: load once + refresh every 5min ───
  useEffect(() => {
    const load = async () => {
      const verified = await fetchJupiterVerified();
      jupVerifiedRef.current = verified;
      // Tag all current tokens
      setTokens(prev => {
        let changed = false;
        const updated = prev.map(t => {
          const isVerified = verified.has(t.addr);
          if (t.jupVerified !== isVerified) { changed = true; return { ...t, jupVerified: isVerified }; }
          return t;
        });
        return changed ? updated : prev;
      });
    };
    load();
    const iv = setInterval(load, 300000); // refresh every 5min
    return () => clearInterval(iv);
  }, []);

  // ─── PUMP.FUN DIRECT + RAYDIUM: deep enrichment cycle ───
  useEffect(() => {
    let deepCycle = 0;
    const iv = setInterval(async () => {
      deepCycle++;
      const cur = tokensRef.current;
      const now = Date.now();

      // === PUMP.FUN DIRECT: bonding curve + community for pre-migration PumpFun tokens ===
      if (deepCycle % 2 === 0) {
        const pumpTokens = cur.filter(t =>
          t.alive && t.qualified && !t.migrated && t.platform === "PumpFun" &&
          (!pumpDirectCache.current[t.addr] || now - pumpDirectCache.current[t.addr].ts > 20000)
        ).slice(0, 3);

        for (const t of pumpTokens) {
          try {
            const pd = await fetchPumpFunDirect(t.addr);
            if (!pd) continue;
            pumpDirectCache.current[t.addr] = { ...pd, ts: now };

            setTokens(prev => prev.map(tok => tok.addr === t.addr ? {
              ...tok,
              bondingPct: pd.bondingPct,
              realSolReserves: pd.realSolReserves,
              replyCount: pd.replyCount,
              isKOTH: pd.isKOTH,
              hasSocials: !!(pd.twitter || pd.telegram || pd.website),
              pumpComplete: pd.complete,
              pumpCreator: pd.creator,
              pumpNsfw: pd.nsfw,
            } : tok));

            console.log(`[PUMP.FUN] 🎰 ${t.name} → ${pd.bondingPct.toFixed(1)}% curve | ${pd.realSolReserves.toFixed(1)} SOL | ${pd.replyCount} replies${pd.isKOTH?" | 👑 KOTH":""}${pd.twitter?" | 🐦":""}${pd.telegram?" | 📱":""}`);
          } catch (e) {
            console.warn("[PUMP.FUN] Failed:", e.message);
          }
        }
      }

      // === RAYDIUM POOL DATA: reserves + LP burn for migrated tokens ===
      if (deepCycle % 3 === 0) {
        const allMigRay = cur.filter(t =>
          t.alive && t.migrated &&
          (!raydiumPoolCache.current[t.addr] || now - raydiumPoolCache.current[t.addr].ts > 25000)
        );
        const rayOffset = Math.floor(deepCycle / 3) * 3 % Math.max(1, allMigRay.length);
        const migrated = allMigRay.slice(rayOffset, rayOffset + 3);

        for (const t of migrated) {
          try {
            const rp = await fetchRaydiumPool(t.addr);
            if (!rp) continue;
            raydiumPoolCache.current[t.addr] = { ...rp, ts: now };

            setTokens(prev => prev.map(tok => tok.addr === t.addr ? {
              ...tok,
              rayTvl: rp.tvl,
              rayVol24h: rp.vol24h,
              rayFees24h: rp.fees24h,
              rayApr24h: rp.apr24h,
              rayBurnPct: rp.burnPct,
              rayPoolId: rp.poolId,
            } : tok));

            setMigrations(prev => prev.map(m => m.mint === t.addr ? {
              ...m, rayTvl: rp.tvl, rayBurnPct: rp.burnPct, rayVol24h: rp.vol24h, rayFees24h: rp.fees24h,
            } : m));

            console.log(`[RAYDIUM] 💎 ${t.name} → TVL:$${(rp.tvl/1000).toFixed(1)}K | Vol24h:$${(rp.vol24h/1000).toFixed(1)}K | LP burn:${rp.burnPct.toFixed(0)}% | Fees:$${rp.fees24h.toFixed(0)}`);
          } catch (e) {
            console.warn("[RAYDIUM] Failed:", e.message);
          }
        }
      }

      // === JUPITER SLIPPAGE: liquidity depth check for locked/qualified tokens ===
      if (deepCycle % 4 === 0) {
        const slipCandidates = cur.filter(t =>
          t.alive && t.qualified && t.mcap > 15000 &&
          (!slippageCache.current[t.addr] || now - slippageCache.current[t.addr].ts > 45000)
        );
        const slipOffset = Math.floor(deepCycle / 4) * 3 % Math.max(1, slipCandidates.length);
        const toCheck = slipCandidates.slice(slipOffset, slipOffset + 3);

        for (const t of toCheck) {
          try {
            const slip = await fetchJupiterSlippage(t.addr, 5000, SOL_USD);
            if (!slip) continue;
            slippageCache.current[t.addr] = { ...slip, ts: now };

            setTokens(prev => prev.map(tok => tok.addr === t.addr ? {
              ...tok,
              slippage5k: slip.slippagePct,
              priceImpact5k: slip.priceImpact,
              liquidityRating: slip.liquidityRating,
            } : tok));

            console.log(`[SLIPPAGE] 💧 ${t.name} → $5K sell = ${slip.slippagePct.toFixed(1)}% slippage | Rating: ${slip.liquidityRating}`);
          } catch (e) {
            console.warn("[SLIPPAGE] Failed:", e.message);
          }
        }
      }

      // === HELIUS SIGNATURES: on-chain activity intensity for top tokens ===
      // Every 4th cycle with rotation to cover more tokens
      if (deepCycle % 4 === 0) {
        const actCandidates = cur.filter(t =>
          t.alive && t.qualified && t.mcap > 10000 &&
          (!activityCache.current[t.addr] || now - activityCache.current[t.addr].ts > 20000)
        );
        const actOffset = Math.floor(deepCycle / 4) * 4 % Math.max(1, actCandidates.length);
        const hotTokens = actCandidates.slice(actOffset, actOffset + 4);

        for (const t of hotTokens) {
          try {
            const sigs = await fetchUniqueSigners(t.addr, 50);

            if (sigs) {
              const updates = {
                activityLevel: sigs.activityLevel,
                txPerMinute: sigs.txPerMinute,
                recentTx5m: sigs.recentTx5m,
                txErrorRate: sigs.errorRate,
              };
              activityCache.current[t.addr] = { ...sigs, ts: now };
              setTokens(prev => prev.map(tok => tok.addr === t.addr ? { ...tok, ...updates } : tok));
            }

            console.log(`[ACTIVITY] 🔥 ${t.name} → ${sigs?.activityLevel||"?"} (${sigs?.recentTx5m||0} tx/5m, ${sigs?.txPerMinute?.toFixed(1)||0}/min)`);
          } catch (e) {
            console.warn("[ACTIVITY] Failed:", e.message);
          }
        }
      }
    }, 12000); // every 12s base cycle
    return () => clearInterval(iv);
  }, []);


  // ═══ SMART MONEY: Score wallets based on WALLET P&L, not token outcome ═══
  useEffect(() => {
    const iv = setInterval(() => {
      const now3 = Date.now();
      // Read tokens from ref — no setTokens needed since we only update walletScores ref
      tokensRef.current.forEach(t => {
          const td = tradeData.current[t.addr];
          if (!td) return;

          // Token state — used only to determine if an open position has expired
          const age = now3 - t.timestamp;
          const tokenDead = ((!t.alive || (t.health || 0) <= 0) && age > 60000) ||
            (age > 120000 && t.mcap < 5000 && !t.migrated) ||
            (age > 180000 && (t.health || 0) < 30 && t.mcap < 20000);
          const tokenAlive = !tokenDead && t.alive;

          Object.entries(td.wallets).forEach(([w, data]) => {
            if (data.bought <= 0.15) return;
            if (!walletScores.current[w]) walletScores.current[w] = {
              wins: 0, losses: 0, holds: 0,
              tokens: [], lossTokens: [], holdTokens: [],   // display names only
              winAddrs: new Set(), lossAddrs: new Set(), holdAddrs: new Set(), // dedup by addr
              totalBought: 0, totalSold: 0, totalPnl: 0, bigWins: 0, trades: [], lastActivity: now3,
            };
            const ws = walletScores.current[w];
            // Migrate old wallets that don't have addr sets yet
            if (!ws.winAddrs)  ws.winAddrs  = new Set();
            if (!ws.lossAddrs) ws.lossAddrs = new Set();
            if (!ws.holdAddrs) ws.holdAddrs = new Set();

            // ─── BOT FILTER — ignore sub-30s flips ───
            const holdDuration = (data.lastSellTime || now3) - (data.firstBuyTime || now3);
            const hasExited = (data.sold || 0) >= data.bought * 0.5;
            if (hasExited && holdDuration < 30000) return;

            // ─── WALLET-LEVEL OUTCOME ───
            // PumpPortal reports solAmount on sells as COST BASIS, not actual SOL received.
            // Fix: use sell event mcap ratios to compute actual SOL out for each sell.
            // Each sellEvent has { sol (cost basis), mcap (mcap at sell time), time }
            // Actual SOL received per sell ≈ cost_basis * (sell_mcap / entry_mcap)

            const entryMcap = data.entryMcap || 0;
            const currentMcap = t.mcap || 0;

            // Filter sell events to this position window, cap so sells can't exceed bought
            const rawSellEvts = (data.sellEvents || []).filter(s =>
              s.time >= (data.firstBuyTime || 0)
            );
            let capRemaining = data.bought;
            const sellEvts = rawSellEvts.filter(s => {
              if (capRemaining <= 0.001) return false;
              const capped = Math.min(s.sol, capRemaining);
              capRemaining -= capped;
              return true;
            });
            const cappedSellEvts = sellEvts; // alias for legacy trade record use

            // Compute actual SOL received per sell using mcap ratio
            let actualSolReceived = 0;
            let costBasisSold = 0;
            for (const s of sellEvts) {
              costBasisSold += s.sol;
              if (entryMcap > 0 && s.mcap > 0) {
                // Bonding curve approximation: actual out = cost_basis * (exit_mcap / entry_mcap)
                actualSolReceived += s.sol * (s.mcap / entryMcap);
              } else {
                actualSolReceived += s.sol; // fallback: treat as break-even if no mcap data
              }
            }

            // Position sizing
            const soldRatioRaw = data.bought > 0 ? costBasisSold / data.bought : 0;
            const positionFullyExited = soldRatioRaw >= 0.90;
            const positionClosed = soldRatioRaw >= 0.50;

            // Remaining position unrealized value
            const remainingCost = Math.max(0, data.bought - costBasisSold);
            const mcapRatio = entryMcap > 0 && currentMcap > 0 ? currentMcap / entryMcap : 1;
            const remainingValue = remainingCost * mcapRatio;
            const unrealizedPnl = remainingValue - remainingCost;

            // PnL: realized (from actual sales) + unrealized (remaining position)
            const realizedPnl = actualSolReceived - costBasisSold;
            const pnl = realizedPnl + (positionFullyExited ? 0 : unrealizedPnl);
            const sold = actualSolReceived; // for trade record compatibility

            const exitMcap = positionClosed ? (data.exitMcap || currentMcap) : currentMcap;

            // WIN: fully exited with profit, OR partially exited with strong realized gain
            const walletWin = (positionFullyExited && realizedPnl > 0) ||
              (positionClosed && realizedPnl > 0 && actualSolReceived >= data.bought * 1.2);
            const walletLoss = !walletWin && (
              (tokenDead && pnl < 0) ||
              (positionFullyExited && realizedPnl < 0)  // use realized only on full exit
            );
            const walletHold = !walletWin && !walletLoss && tokenAlive;

            // Sell events for trade records
            const sellEvents = cappedSellEvts;

            // ── WIN ──
            if (walletWin && !ws.winAddrs.has(t.addr)) {
              if (ws.holdAddrs.has(t.addr)) {
                ws.holds = Math.max(0, ws.holds - 1);
                ws.holdAddrs.delete(t.addr);
                ws.holdTokens = (ws.holdTokens||[]).filter(a => a !== t.addr);
                const holdTrade = ws.trades.find(tr => tr.addr === t.addr && tr.type === "HOLD");
                if (holdTrade) { holdTrade.type = "WIN"; holdTrade.mcap = data.exitMcap||exitMcap; holdTrade.sold = sold; holdTrade.pnl = pnl; holdTrade.athMcap = Math.max(td.athMcap||0, data.exitMcap||exitMcap, holdTrade.athMcap||0); holdTrade.sellEvents = sellEvents; }
              } else {
                ws.trades.push({ token: t.name, addr: t.addr, sol: data.bought, sold: sold, type: "WIN", mcap: exitMcap, entryMcap, pnl,
                  entryTime: data.firstBuyTime || now3, exitTime: data.lastSellTime || now3,
                  athMcap: td.athMcap || exitMcap, startMcap: td.startMcap || entryMcap, time: now3, sellEvents });
                if (ws.trades.length > 50) ws.trades = ws.trades.slice(-50);
              }
              ws.wins++;
              ws.winAddrs.add(t.addr);
              ws.tokens.push(t.name); // display name — winAddrs Set handles dedup
              ws.totalBought += data.bought;
              ws.totalSold += sold; // use corrected sold not raw data.sold
              ws.totalPnl += pnl;
              if (exitMcap > 100000) ws.bigWins = (ws.bigWins || 0) + 1;
              if (ws.wins === 3) console.log(`[SMART$] 🧠 Wallet ${w.slice(0,8)} hit 3 wins`);
              if (ws.wins === 6) console.log(`[SMART$] 🧠🧠 Wallet ${w.slice(0,8)} hit 6 WINS`);
              td.wallets[w] = { bought: 0, sold: 0, firstBuyTime: null, lastSellTime: null, entryMcap: 0, exitMcap: 0, sellEvents: [] };
              // Keep addr in winAddrs — never delete, prevents same position re-triggering as win
              if (ws.activeBuys) delete ws.activeBuys[t.addr];
              sbUpsertWallet(w, walletScores.current[w]);
            }

            // ── LOSS ──
            if (walletLoss && !ws.lossAddrs.has(t.addr) && !ws.winAddrs.has(t.addr)) {
              if (ws.holdAddrs.has(t.addr)) {
                ws.holds = Math.max(0, ws.holds - 1);
                ws.holdAddrs.delete(t.addr);
                ws.holdTokens = (ws.holdTokens||[]).filter(a => a !== t.addr);
                const holdTrade = ws.trades.find(tr => tr.addr === t.addr && tr.type === "HOLD");
                if (holdTrade) { holdTrade.type = "LOSS"; holdTrade.mcap = data.exitMcap||exitMcap; holdTrade.sold = sold; holdTrade.pnl = pnl; holdTrade.athMcap = Math.max(td.athMcap||0, data.exitMcap||exitMcap, holdTrade.athMcap||0); holdTrade.sellEvents = sellEvents; }
              } else {
                ws.trades.push({ token: t.name, addr: t.addr, sol: data.bought, sold: sold, type: "LOSS", mcap: exitMcap, entryMcap, pnl,
                  entryTime: data.firstBuyTime || now3, exitTime: data.lastSellTime || now3,
                  athMcap: td.athMcap || exitMcap, startMcap: td.startMcap || entryMcap, time: now3, sellEvents });
                if (ws.trades.length > 50) ws.trades = ws.trades.slice(-50);
              }
              ws.losses++;
              ws.lossAddrs.add(t.addr);
              ws.lossTokens.push(t.name); // display name
              ws.totalBought += data.bought;
              ws.totalSold += sold;
              ws.totalPnl += pnl;
              td.wallets[w] = { bought: 0, sold: 0, firstBuyTime: null, lastSellTime: null, entryMcap: 0, exitMcap: 0, sellEvents: [] };
              // Keep addr in lossAddrs — never delete
              if (ws.activeBuys) delete ws.activeBuys[t.addr];
              sbUpsertWallet(w, walletScores.current[w]);
            }

            // ── HOLD ──
            if (walletHold && !ws.winAddrs.has(t.addr) && !ws.lossAddrs.has(t.addr) && !ws.holdAddrs.has(t.addr)) {
              ws.holds = (ws.holds || 0) + 1;
              ws.holdAddrs.add(t.addr);
              ws.holdTokens = ws.holdTokens || [];
              ws.holdTokens.push(t.addr); // store addr not name
              ws.trades.push({ token: t.name, addr: t.addr, sol: data.bought, sold: sold, type: "HOLD", mcap: exitMcap, entryMcap, pnl,
                entryTime: data.firstBuyTime || now3, exitTime: null,
                athMcap: td.athMcap || exitMcap, startMcap: td.startMcap || entryMcap, time: now3, sellEvents });
              if (ws.trades.length > 50) ws.trades = ws.trades.slice(-50);
              sbUpsertWallet(w, walletScores.current[w]);
            }
            // Refresh live HOLD entries with current mcap/pnl/athMcap/sellEvents
            if (walletHold && ws.holdAddrs.has(t.addr)) {
              const existTrade = ws.trades.find(tr => tr.addr === t.addr && tr.type === "HOLD");
              if (existTrade) {
                existTrade.mcap = exitMcap;
                existTrade.sold = sold;            // actual SOL received from sells so far
                existTrade.pnl = pnl;              // realized + unrealized combined
                existTrade.realizedPnl = realizedPnl;
                existTrade.unrealizedPnl = unrealizedPnl;
                existTrade.remainingCost = remainingCost; // SOL still in position
                existTrade.athMcap = Math.max(td.athMcap||0, existTrade.athMcap||0);
                existTrade.sellEvents = cappedSellEvts;
              }
            }
          });
        });
      // ── SMART PRUNING ──
      const keys = Object.keys(walletScores.current);
      // Hard cap
      if (keys.length > 5000) {
        const sorted = keys.sort((a, b) => (walletScores.current[b]?.wins || 0) - (walletScores.current[a]?.wins || 0));
        const keep = new Set(sorted.slice(0, 2000));
        keys.forEach(k => { if (!keep.has(k)) delete walletScores.current[k]; });
      }
      // Purge spray-and-pray wallets (10:1+ loss:win ratio)
      // Purge no-win no-hold wallets after 2 min of inactivity
      // Purge 1W/1L/0H wallets after 5 min of inactivity
      Object.keys(walletScores.current).forEach(k => {
        const ws = walletScores.current[k];
        const idle = now3 - (ws.lastActivity || 0);
        if (ws.losses > 10 && ws.wins > 0 && ws.losses / ws.wins >= 10) {
          delete walletScores.current[k]; return;
        }
        // No wins, no holds, only losses — purge after 2 min idle
        if (ws.wins === 0 && (ws.holds || 0) === 0 && idle > 120000) {
          delete walletScores.current[k]; return;
        }
        // 1 win, 1+ loss, no holds — mediocre, purge after 5 min idle
        if (ws.wins <= 1 && ws.losses >= 1 && (ws.holds || 0) === 0 && idle > 300000) {
          delete walletScores.current[k]; return;
        }
        // 0 wins, 0 losses, only holds that never resolved — purge after 10 min idle
        if (ws.wins === 0 && ws.losses === 0 && idle > 600000) {
          delete walletScores.current[k]; return;
        }
      });

      // ── PRUNE REFS for dead/removed tokens (every 30s) ──
      if (now3 % 30000 < 3000) {
        var liveAddrs = new Set(tokensRef.current.map(t => t.addr));
        // tradeData — keep only live tokens + recently scored wallets (via walletScores)
        Object.keys(tradeData.current).forEach(addr => {
          if (!liveAddrs.has(addr)) delete tradeData.current[addr];
        });
        // mintToId — keep only live tokens
        Object.keys(mintToId.current).forEach(mint => {
          if (!liveAddrs.has(mint)) delete mintToId.current[mint];
        });
        // firedEvents — cap at 500
        if (firedEvents.current.size > 500) {
          var arr = [...firedEvents.current];
          firedEvents.current = new Set(arr.slice(-300));
        }
        // copyTradeAlerts — cap at 500
        if (copyTradeAlerts.current.size > 500) {
          var arr2 = [...copyTradeAlerts.current];
          copyTradeAlerts.current = new Set(arr2.slice(-300));
        }
        // bondingAlerted — prune dead tokens
        bondingAlerted.current.forEach(addr => {
          if (!liveAddrs.has(addr)) bondingAlerted.current.delete(addr);
        });
        // cache refs — prune dead tokens
        [rugCheckCache, curveProgressCache, slippageCache, pumpDirectCache, raydiumPoolCache, activityCache].forEach(cache => {
          Object.keys(cache.current).forEach(addr => {
            if (!liveAddrs.has(addr)) delete cache.current[addr];
          });
        });
        // flashSnapsRef, fastSnapRef — prune dead tokens
        Object.keys(flashSnapsRef.current).forEach(addr => {
          if (!liveAddrs.has(addr)) delete flashSnapsRef.current[addr];
        });
        Object.keys(fastSnapRef.current).forEach(addr => {
          if (!liveAddrs.has(addr)) delete fastSnapRef.current[addr];
        });
      }
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  // ═══ NARRATIVE CLUSTERING: Detect trending themes ═══
  useEffect(() => {
    const iv = setInterval(() => {
      setTokens(prev => {
        const recent = prev.filter(t => Date.now() - t.timestamp < 300000); // last 5 min
        if (recent.length < 5) return prev;
        // Extract keywords from names
        const keywords = {};
        const stopWords = new Set(["the","of","to","a","in","is","it","for","on","inu","token","coin","sol"]);
        recent.forEach(t => {
          const words = (t.name + " " + (t.fullName || "")).toLowerCase()
            .replace(/[^a-z0-9\s]/g, "").split(/\s+/)
            .filter(w => w.length > 2 && !stopWords.has(w));
          words.forEach(w => {
            if (!keywords[w]) keywords[w] = { count: 0, tokens: [], totalMcap: 0 };
            if (!keywords[w].tokens.find(x => x.name === t.name)) {
              keywords[w].count++;
              keywords[w].tokens.push({ name: t.name, mcap: t.mcap, qualified: t.qualified });
              keywords[w].totalMcap += t.mcap;
            }
          });
        });
        // Find trending (3+ tokens with same keyword)
        const trending = Object.entries(keywords)
          .filter(([, v]) => v.count >= 3)
          .sort((a, b) => b[1].count - a[1].count)
          .slice(0, 8)
          .map(([word, data]) => ({
            word, count: data.count, tokens: data.tokens.slice(0, 5),
            avgMcap: data.totalMcap / data.count,
            qualified: data.tokens.filter(t => t.qualified).length,
          }));
        if (trending.length > 0) setNarratives(trending);
        return prev;
      });
    }, 15000);
    return () => clearInterval(iv);
  }, []);

  // ═══ SESSION STATS ═══
  useEffect(() => {
    const iv = setInterval(() => {
      setTokens(prev => {
        const qualified = prev.filter(t => t.qualified).length;
        const dead = prev.filter(t => !t.alive || (t.health || 0) <= 0).length;
        const alive = prev.filter(t => t.alive && (t.health || 0) > 0);
        let best = null, bestPct = 0;
        alive.forEach(t => {
          if (t.mcap > 30000 && t.mcap > bestPct) { best = t.name; bestPct = t.mcap; }
        });
        setSessionStats(s => ({
          ...s, tokensScanned: prev.length, qualified, dead,
          bestToken: best || s.bestToken, bestPct: bestPct || s.bestPct,
          smartWallets: Object.values(walletScores.current).filter(w => {
            const wins=(w.trades||[]).filter(tr=>tr.type==="WIN").length;
            const losses=(w.trades||[]).filter(tr=>tr.type==="LOSS").length;
            const total=wins+losses;
            const rate=total>0?wins/total:0;
            const unrealized=(w.trades||[]).filter(tr=>tr.type==="HOLD").reduce((s,tr)=>s+(tr.unrealizedPnl!=null?tr.unrealizedPnl:(tr.pnl!=null?tr.pnl:0)),0);
            const adjustedPnl=(w.totalPnl||0)+unrealized;
            return wins>=4&&rate>=0.60&&wins>=(losses*2)&&total>=5&&(w.totalPnl||0)>=1.0&&adjustedPnl>0.5;
          }).length,
          solPrice: SOL_USD, mcapCorr: MCAP_CORRECTION,
        }));
        return prev;
      });
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  // ═══ FAST SNAP: 5s ring-buffer from tradeData mcap — fills board gaps between DexScreener batches ═══
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      tokensRef.current.forEach(t => {
        if (!t.alive || !t.mcap || t.mcap <= 0) return;
        const addr = t.addr;
        if (!fastSnapRef.current[addr]) fastSnapRef.current[addr] = [];
        fastSnapRef.current[addr].push({ time: now, mcap: t.mcap });
        // Keep 6 minutes of data for 5m board
        fastSnapRef.current[addr] = fastSnapRef.current[addr].filter(s => now - s.time < 370000);
      });
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  // ═══ FLASH BOARDS: 30s + 1m + 5m mcap movers — the danger zones ═══
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      const compute = (windowMs) => {
        const target = now - windowMs;
        // Merge DexScreener snaps + fast snaps for maximum resolution
        return tokensRef.current
          .filter(t => t.alive && t.qualified && t.mcap > 0)
          .map(t => {
            const dexSnaps = flashSnapsRef.current[t.addr] || [];
            const fastSnaps = fastSnapRef.current[t.addr] || [];
            const allSnaps = [...dexSnaps, ...fastSnaps].sort((a, b) => a.time - b.time);
            if (allSnaps.length < 2) return null;
            const snap = allSnaps.reduce((best, s) => {
              if (!best) return s;
              return Math.abs(s.time - target) < Math.abs(best.time - target) ? s : best;
            }, null);
            if (!snap || snap.mcap <= 0) return null;
            if (now - snap.time < windowMs * 0.5) return null;
            const gain = ((t.mcap - snap.mcap) / snap.mcap) * 100;
            if (Math.abs(gain) < 0.5) return null;
            const sigScore = signalScores.current[t.addr] || 0;
            const extBoost = externalBoostRef.current[t.addr]?.boost || 0;
            return {
              addr: t.addr, name: t.name, gain,
              mcap: t.mcap, startMcap: snap.mcap,
              threatColor: t.threatColor || '#00ffff',
              sigScore: Math.min(100, sigScore + extBoost),
              hasSmartMoney: t.hasSmartMoney,
              smartWalletCount: t.smartWalletCount || 0,
              isCluster: !!t._hotCluster,
              qualified: t.qualified,
            };
          })
          .filter(Boolean)
          .sort((a, b) => b.gain - a.gain);
      };
      setFlashBoard30s(compute(30000).slice(0, 10));
      setFlashBoard1m(compute(60000).slice(0, 10));
      setFlashBoard5m(compute(300000).slice(0, 10));
    }, 1000); // refresh every 1s — these boards are LIVE
    return () => clearInterval(iv);
  }, []);

  // ═══ NEURAL SIGNAL SCORES: composite 0-100 per token, updated every 2s ═══
  useEffect(() => {
    const prevScores = {};
    const autoSurfaceCooldown = {}; // addr -> lastSurfaceTime (throttle 60s)
    const iv = setInterval(() => {
      const scores = {};
      const newSurfaces = [];
      const now = Date.now();
      tokensRef.current.forEach(t => {
        if (!t.alive) return;
        let s = 0;
        // ── Qualification backbone ──
        s += (t.qualScore || 0) * 6;                          // 0-48
        // ── Smart money (most predictive single signal) ──
        s += Math.min(24, (t.smartWalletCount || 0) * 8);
        // ── Momentum ──
        if (t.accelerating) s += 12;
        s += Math.min(10, (t.velocity || 0) * 1.0);
        // ── Token health ──
        if ((t.retentionPct || 0) > 70) s += 7;
        else if ((t.retentionPct || 0) > 50) s += 3;
        if ((t.smallBuyRatio || 0) > 60) s += 5;
        if ((t.freshPct || 100) < 40) s += 5;
        // ── On-chain security ──
        if (t.lpLocked) s += 4;
        if ((t.rayBurnPct || 0) > 80) s += 5;
        // ── Cross-platform signals ──
        if (t.onGeckoTrending) s += 9;
        if ((t.trendScore || 0) >= 2) s += 7;
        if (t.hasSmartMoney) s += 5;
        // ── Post-migration activity ──
        if (t.migrated && (t.geckoVol1h || 0) > 10000) s += 9;
        if (t.migrated && (t.rayTvl || 0) > 20000) s += 6;
        if (t.activityLevel === 'BLAZING') s += 9;
        else if (t.activityLevel === 'HOT') s += 5;
        else if (t.activityLevel === 'ACTIVE') s += 2;
        // ── Dev track record ──
        if (t.deployerGrade === 'A') s += 6;
        else if (t.deployerGrade === 'B') s += 2;
        if (t.isKOTH) s += 4;
        if (t.hasSocials) s += 2;
        if (t.replyCount > 20) s += 3;
        if ((t.bondingPct || 0) > 80 && !t.migrated) s += 8; // migration pump incoming
        // ── INTEL BOOST: cluster + DNA composite (injected from useIntelligence) ──
        const extBoost = externalBoostRef.current[t.addr]?.boost || 0;
        s += extBoost;
        if (t._hotCluster) s += 12; // active coordinated buy = major signal
        // ── Penalties ──
        if (t.bundleDetected) s -= 28;
        if (t.deployerGrade === 'F') s -= 22;
        if (t.deployerGrade === 'D') s -= 12;
        if (t.liquidityRating === 'PAPER') s -= 12;
        if (t.rugLevel === 'DANGER') s -= 18;
        if (t.isSerialRugger) s -= 35;
        if (t.isStale || t.isDead) s -= 15;
        // ── MCAP RETRACE PENALTY — coin that pumped and dumped back should NOT score high ──
        const peak = t.peakMcap || t.mcap || 0;
        const cur_mc = t.mcap || 0;
        if (peak > 0 && cur_mc > 0) {
          const retracePct = (peak - cur_mc) / peak; // 0 = at peak, 1 = totally collapsed
          if (retracePct > 0.6) s -= 40;         // >60% retrace from peak = dead
          else if (retracePct > 0.4) s -= 20;    // >40% retrace = fading
          else if (retracePct > 0.25) s -= 8;    // >25% retrace = cooling
        }

        // ── TRADE SILENCE DECAY — no recent trades = score decays ──
        const td_s = tradeData.current[t.addr];
        const lastTrade = td_s?.lastTradeTime || t.timestamp || 0;
        const silentSec = (now - lastTrade) / 1000;
        if (silentSec > 300) s -= 35;       // 5min silence = nearly dead
        else if (silentSec > 120) s -= 18;  // 2min silence = cooling
        else if (silentSec > 60) s -= 8;    // 1min silence = slowing

        scores[t.addr] = Math.max(0, Math.min(100, Math.round(s)));

        // ── AUTO-SURFACE: emit when score crosses 92+ with hard quality gates ──
        const prev = prevScores[t.addr] || 0;
        const cur = scores[t.addr];
        const lastSurface = autoSurfaceCooldown[t.addr] || 0;

        // Hard gates — reject trash before it can surface
        const meetsGates = (
          (t.mcap || 0) >= 8000                              // min $8K mcap — no dust
          && (t.qualScore || 0) >= 6                        // must be near-qualified
          && !t.bundleDetected                              // no bundles ever
          && t.deployerGrade !== 'F' && t.deployerGrade !== 'D' // no known ruggers
          && !t.isStale && !t.isDead                        // must be alive and trading
          && (t.liquidityRating !== 'PAPER')                // must have real liquidity
          && (
            (t.smartWalletCount || 0) >= 2                  // 2+ smart wallets, OR
            || (t.accelerating && t._hotCluster)            // accel + cluster combo, OR
            || (t.onGeckoTrending && (t.qualScore||0) >= 7) // gecko trending + strong qual
          )
        );

        if (cur >= 88 && prev < 88 && now - lastSurface > 90000 && meetsGates) {
          autoSurfaceCooldown[t.addr] = now;
          const extData = externalBoostRef.current[t.addr];
          const reasons = [];
          if ((t.smartWalletCount||0) >= 2) reasons.push(`🧠 ${t.smartWalletCount}x smart wallets`);
          if (t._hotCluster) reasons.push(`🔗 CLUSTER detected`);
          if (t.accelerating) reasons.push(`⚡ accelerating`);
          if (t.onGeckoTrending) reasons.push(`🦎 gecko trending`);
          if ((t.bondingPct || 0) > 80 && !t.migrated) reasons.push(`🚀 ${t.bondingPct?.toFixed(0)}% bonding`);
          if (t.activityLevel === 'BLAZING') reasons.push(`🔥 blazing`);
          if (t.deployerGrade === 'A') reasons.push(`✅ PROVEN dev`);
          if (extData?.reasons?.length) reasons.push(...extData.reasons.slice(0, 2));
          newSurfaces.push({
            id: Date.now() + Math.random(),
            addr: t.addr, name: t.name, score: cur,
            mcap: t.mcap, reasons, time: now,
            threatColor: t.threatColor || '#00ffff',
          });
        }
        prevScores[t.addr] = cur;
      });
      signalScores.current = scores;
      if (newSurfaces.length > 0) {
        setAutoSurface(prev => [...newSurfaces, ...prev].slice(0, 8));
      }
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  return { tokens, whaleAlerts, intelEvents, migrations, stats,
    smartMoneyAlerts, bundleAlerts, narratives, sessionStats, walletScoresRef: walletScores,
    tradeDataRef: tradeData, deployerHistoryRef: deployerHistory,
    flashBoard30s, flashBoard1m, flashBoard5m, autoSurface,
    signalScoresRef: signalScores, externalBoostRef, flashSnapsRef };
}
